/**
 * Player élève V2 — résolution pas-à-pas d'un exercice V2.
 *
 * Modèle V2 : la section `statement` contient une suite ordonnée de blocs
 *   - `statement`         → bloc de texte (situation, consigne intermédiaire…)
 *   - `statement_question`→ une question (corrigée par 1..N sous-QCM = étapes)
 *
 * Le premier `statement` (s'il existe) est la **situation** — affichée en
 * permanence en haut. Le reste des blocs de la section sont parcourus via le
 * bouton « Suivant ». Une question dont le corrigé est PAS-À-PAS se déploie en
 * plusieurs pas (un par checkpoint/étape, cf. `buildPlaySteps`) : chaque étape
 * est un sous-QCM (résultat intermédiaire + ses distracteurs), la dernière
 * portant la réponse finale. Le bouton n'est dispo qu'une fois l'étape répondue.
 *
 * Chargement : RPC `get_exercise_v2_for_student(exercise_id)`.
 * Soumission : RPC `submit_block_answer` (par QCM) puis `finish_attempt`.
 *
 * UI mobile-first : cover en haut, boutons pleine largeur, padding pb-safe.
 */

import { withTrousseWheel } from '@/features/trousse-collection/wheel-end-button';
import { publishProgress } from '@/lib/progress-sync';
import {
  StartAttemptError,
  parseStartAttempt,
  startAttemptErrorMessage,
} from '@/lib/exercise-v2/start-attempt';
import { CardXpGain, CardXpGainList } from '@/features/skillcards/card-xp-gain';
import { SkillcardExerciseCover } from '@/features/skillcards/skillcard-cover';
import { ResultAura } from '@/features/skillcards/result-aura';
import { ArrowLeft, CheckCircle2, Flag, Lock } from 'lucide-react';
import { ReportQuestionSheet } from './report-question-sheet';
import {
  cardXpGains,
  cardsForQuestion,
  useExerciseCardLinks,
  mainCardOfLinks,
  useStudentSkillcards,
  type CardXpGainItem,
} from '@/lib/cards/student-skillcards';
import {
  Fragment,
  Suspense,
  lazy,
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
} from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import {
  cn,
  Button,
  useDelayedReveal,
  READING_REVEAL_DELAY_MS,
  FeedbackPopup,
  CelebrationPopup,
  computeXpReward,
  type XpReward,
  MathText,
} from '@jeraisonne/ui';
import { supabase } from '@/lib/supabase';
import type { SupabaseClient } from '@supabase/supabase-js';
import { useAuth } from '@/lib/auth';
import { TrousseToolsProvider } from '@/features/trousse/trousse-tools';
import { ExerciseChallengeButton } from '@/features/private-live/exercise-challenge';
import { MarkdownMath } from '@jeraisonne/ui';
import { VisualBlocksRenderer, VisualBlockRenderer } from '@jeraisonne/ui';
import type { ExerciseVisualBlock } from '@jeraisonne/ai';
import type { ExerciseBlock, ExerciseV2Payload } from '@/lib/exercise-v2/types';
import {
  blocksInSection,
  childrenOf,
  buildPlaySteps,
  orderedCheckpointsFor,
  checkpointAnswerMode,
  correctChoiceIds,
  isAnswerSetCorrect,
  calculatorCheckpointData,
  visualsOf,
  type PlayStep,
} from '@/lib/exercise-v2/block-helpers';
import { airSentences, isTitleEcho } from '@/lib/exercise-v2/text-layout';
import {
  ExerciseQuickNav,
  getBlockIcon,
  BookOpen,
  HelpCircle,
  type QuickNavItem,
} from '@/components/exercise-quick-nav';
import { CalculatorModal } from '@/components/calculator-modal';
import { CardLessonPopup } from '@/features/skillcards/card-lesson-popup';
import { IntroRevision } from './intro-revision';
import { sheetOpensByDefault } from '@/features/scratch-sheet/auto-open';
import { usePublicCards } from '@/lib/cards/api';
import {
  ToolboxSheet,
  contextForCards,
  toolsForContext,
  type ToolId,
  type ToolboxContext,
} from '@/features/toolbox';
import { ChallengeShareModal } from '@/components/challenge-share-modal';
import { ExercisePaywallCard } from '@/components/exercise-paywall-card';
import { formatTimer } from '@/lib/exercise-v2/format-timer';
import {
  endsWithSkillcardSample,
  isExerciseLocked,
  isLockedUpfront,
  isSkillcardPaywall,
  remainingLockedQuestions,
  type V2Access,
} from '@/lib/exercise-v2/access';
import { SkillcardPaywallCard } from '@/components/skillcard-paywall-card';
import { consumeExerciseExit, rememberExerciseExit } from '@/lib/exercise-exit';
import { useExerciseExitGuard } from './use-exit-guard';
import { ExitConfirmSheet } from './exit-confirm-sheet';
import { CountUpNumber } from '@/components/count-up-number';
import {
  answersFromAttemptSteps,
  rememberResume,
  planAutoResume,
  elapsedFromAttemptSteps,
  resumeStepIndex,
  takeResume,
  type ExerciseResume,
} from '@/lib/exercise-v2/resume';
import { SkillcardUnlockCover } from '@/features/skillcards/skillcard-unlock-cover';
import { skillcardBuyPath } from '@/lib/skillcards/purchase';
import { SkillcardOffersPanel } from '@/features/skillcards/skillcard-offers-panel';
import { isNativeApp } from '@/lib/platform';
import { canBuySkillcardsInApp } from '@/lib/skillcards/iap';
import { sendNotification } from '@/lib/notifications/send-notification';
import { completionNotification } from '@/lib/notifications/completion';
import { ClueHuntProvider } from './clue-hunt/ClueHuntContext';
import { ClueHuntStickyBar } from './clue-hunt/ClueHuntStickyBar';
import { ClueHuntCasesInline } from './clue-hunt/ClueHuntCasesInline';
import { ClueTargetsRenderer } from './clue-hunt/ClueTargetsRenderer';
import { FloatingXpBadge } from './clue-hunt/FloatingXpBadge';
import { CluePopup } from './clue-hunt/CluePopup';
import { useClueHuntCtx } from './clue-hunt/ClueHuntContext';
import { VariableExpressionAnswer } from './variable-expression-answer';
import { buildQcmOptions } from '@/features/paper-player/qcm-options';
import {
  AnswerDock,
  AnswerKeys,
  PaperChoiceList,
  PaperColumn,
  PaperNextButton,
  PaperPage,
  dockMode,
  type DockMode,
  usePaperPlayer,
  usePaperQcm,
} from '@/features/paper-player';
import { isFirstScratchUse } from '@/features/scratch-tutorial/first-use';
import {
  FinalTeacherGrade,
  TeacherNote,
  TeacherScoreStamp,
  teacherAppreciation,
} from '@jeraisonne/ui/paper';
import { useStudentProfile } from '@/hooks/use-student-profile';
import {
  ScratchEntryCard,
  ScratchLinesPreview,
  ScratchSheet,
  InlineSheet,
  PaperTutorial,
  hasSeenPaperTutorial,
  SheetPalette,
  useSessionState,
  type ScratchSession,
  useFoundChoices,
  useCrossedChoices,
  isFreeSheetLevel,
  numbersFromBlocks,
  freeModelInputOf,
  useScratchSheets,
  calculatorCost,
} from '@/features/scratch-sheet';
import type { ClueHuntData } from '@jeraisonne/db';
import { PageLoader, Spinner } from '@/components/spinner';
import { streamPending, useExerciseStream } from '@/features/exercise-stream/use-exercise-stream';
import {
  StreamConsentDialog,
  StreamPreview,
  StreamRecordingActions,
  StreamResultActions,
} from '@/features/exercise-stream/stream-ui';

const V2_ENONCE_ANCHOR = 'exercise-v2-enonce';
const V2_QUESTION_ANCHOR = 'exercise-v2-question';

interface PlayerExercise {
  id: string;
  chapter_id?: string;
  title: string;
  difficulty: 'easy' | 'medium' | 'hard';
  estimated_minutes: number | null;
  illustration_url: string | null;
  content_version: 'v1' | 'v2';
  /** Exercice fondamental (style Warmup) : questions INDÉPENDANTES, séparées à l'écran. */
  is_fundamental?: boolean;
}

/**
 * Séparateur entre deux questions indépendantes (exercice fondamental) : sans
 * lui, l'énoncé précédent se lit comme le contexte de la question suivante.
 */
export function QuestionSeparator({ index, total }: { index: number; total: number }) {
  return (
    <div
      role="separator"
      aria-label={`Question ${index} sur ${total}`}
      className="flex items-center gap-3 !rounded-none border-0 bg-transparent py-4"
    >
      <span aria-hidden className="bg-ink-200 h-px flex-1" />
      <span className="font-display text-primary-600 text-[11px] font-semibold uppercase tracking-widest">
        Question {index} / {total}
      </span>
      <span aria-hidden className="bg-ink-200 h-px flex-1" />
    </div>
  );
}

interface PlayerPayload {
  exercise?: PlayerExercise;
  blocks?: ExerciseBlock[];
  access?: V2Access;
  attempt_id?: string | null;
  error?: string;
}

export interface ExercisePlayerV2Props {
  exerciseId: string;
  /**
   * Mode prévisualisation (tuteur « Tester »). Aucun appel à `start_attempt`,
   * `submit_block_answer` ni `finish_attempt` — la justesse des QCM est
   * recalculée localement. Charge l'exercice via `exercise-get-for-edit`
   * (qui accepte les exos draft/awaiting_review du tuteur).
   */
  previewMode?: boolean;
  /** Callback de sortie du mode preview (cliqué dans le bandeau « Quitter »). */
  onExitPreview?: () => void;
  /**
   * Mode démo anonyme (route `/demo/exercise/$id`). Comme `previewMode` côté
   * mutations (aucun appel base), mais :
   *   - les blocs sont fournis pré-tronqués par la RPC anon `get_exercise_v2_demo`
   *     (3 premières QUESTIONS, pas 3 premières étapes — `statement` purs ne
   *     comptent pas) ;
   *   - à la fin du dernier bloc, on déclenche `onDemoComplete` (paywall) au
   *     lieu d'afficher le bilan.
   */
  demoMode?: boolean;
  /** Payload pré-fetché par le loader anon V2 (sert quand `demoMode` est vrai). */
  demoPayload?: PlayerPayload;
  /** Callback à la sortie de la dernière question en démo (route → paywall). */
  onDemoComplete?: () => void;
}

const DIFF_META = {
  easy: { label: 'Facile', cls: 'bg-xp-50 text-xp-600' },
  medium: { label: 'Moyen', cls: 'bg-level-50 text-level-600' },
  hard: { label: 'Difficile', cls: 'bg-accent-50 text-accent-600' },
} as const;

type UntypedRpc = (
  name: string,
  params: Record<string, unknown>,
) => Promise<{ data: unknown; error: Error | null }>;

interface AnswerRecord {
  correctionId: string;
  /** Choix primaire (premier coché) — utilisé pour le partage « Défi ». Vide en mode calculatrice. */
  chosen_id: string;
  /** Ensemble des choix cochés (1 en mode single, ≥1 en mode multiple). Vide en mode calculatrice. */
  chosen_ids: string[];
  was_correct: boolean;
  /** Valeur soumise en mode de réponse "calculator" (générateur paramétrique Blueprint). */
  answer_value?: number;
}

function hashString(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = (h * 33) ^ s.charCodeAt(i);
  return Math.abs(h) || 1;
}

// ─── Cover (réplique l'ExerciseHeader V1) ─────────────────────────────────────

/**
 * Retour : page précédente, ou l'accueil de l'app quand l'exercice a été ouvert
 * directement (lien partagé, notification) — sinon le bouton ne ferait rien.
 */
function goBack(exerciseId?: string) {
  // Retour d'un paiement : l'historique passe par Stripe et la page d'achat →
  // on retourne directement à la page qui avait ouvert l'exercice.
  const exit = exerciseId ? consumeExerciseExit(exerciseId) : null;
  if (exit) window.location.replace(exit);
  else if (window.history.length > 1) window.history.back();
  else window.location.assign('/');
}

function useExerciseCover({
  exerciseId,
  chapterId,
  fallback,
}: {
  exerciseId: string;
  chapterId?: string;
  fallback: string | null;
}) {
  return useQuery({
    queryKey: ['exercise-v2-cover', exerciseId, chapterId ?? null],
    enabled: !!exerciseId,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<string | null> => {
      if (fallback) return fallback;
      if (!chapterId) return null;
      const { data } = await supabase
        .from('chapters')
        .select('illustration_url')
        .eq('id', chapterId)
        .maybeSingle();
      return (data as { illustration_url: string | null } | null)?.illustration_url ?? null;
    },
  });
}

/**
 * Titre de l'épreuve à laquelle appartient l'exercice (table `epreuve_exercises`).
 * Sert de surtitre dans la cover de repli quand un exo d'épreuve n'a pas
 * d'illustration. Requêté seulement dans ce cas (ni illustration, ni chapitre).
 */
function useEpreuveTitle({ exerciseId, enabled }: { exerciseId: string; enabled: boolean }) {
  return useQuery({
    queryKey: ['exercise-epreuve-title', exerciseId],
    enabled: !!exerciseId && enabled,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<string | null> => {
      const { data } = await supabase
        .from('epreuve_exercises')
        .select('epreuves(title)')
        .eq('exercise_id', exerciseId)
        .maybeSingle();
      return (data as { epreuves: { title: string } | null } | null)?.epreuves?.title ?? null;
    },
  });
}

function ExerciseHeader({
  exerciseId,
  chapterId,
  illustrationUrl,
  title,
  progress,
  elapsedLabel,
}: {
  exerciseId: string;
  chapterId?: string;
  illustrationUrl: string | null;
  /** Titre de l'exercice — affiché centré dans la cover quand pas d'illustration. */
  title?: string;
  progress?: number;
  /** Chrono affiché en haut à droite (ex. `7:03`). Absent = pas de chrono. */
  elapsedLabel?: string;
}) {
  const { data: cover, isLoading: coverLoading } = useExerciseCover({
    exerciseId,
    chapterId,
    fallback: illustrationUrl,
  });
  // Pas d'illustration → cover aux couleurs de la SKILLCARD principale de
  // l'exo (même requête que les gains HORA : déjà en cache).
  const { data: cardLinks } = useExerciseCardLinks(exerciseId, !coverLoading && !cover);
  const skillcard = !cover && cardLinks ? mainCardOfLinks(cardLinks) : null;
  // Pas d'illustration ni de chapitre → exo d'épreuve : on récupère le titre de
  // l'épreuve pour la cover de repli.
  const { data: epreuveTitle } = useEpreuveTitle({
    exerciseId,
    enabled: !cover && !illustrationUrl && !chapterId && !skillcard,
  });
  return (
    <header className="mx-auto max-w-2xl lg:max-w-4xl">
      <div className="from-primary-50 bg-linear-to-br to-ink-50 relative aspect-video w-full overflow-hidden sm:mt-4 sm:rounded-2xl">
        {cover && (
          <img
            src={cover}
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          />
        )}
        <div
          aria-hidden="true"
          className="bg-linear-to-t from-ink-50 pointer-events-none absolute inset-x-0 bottom-0 h-1/3 to-transparent"
        />
        {/* Exo relié à une SKILLCARD sans illustration : cover de la carte. */}
        {skillcard && <SkillcardExerciseCover card={skillcard} exerciseTitle={title} />}
        {/* Cover de repli : pas d'illustration → titre de l'épreuve + titre de
            l'exo, centrés. */}
        {!cover && !skillcard && title && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1.5 px-6 py-12 text-center">
            {epreuveTitle && (
              <p className="font-display text-[11px] font-semibold uppercase tracking-widest text-white/70 drop-shadow">
                {epreuveTitle}
              </p>
            )}
            <p className="font-display text-lg font-extrabold leading-tight text-white drop-shadow-md sm:text-xl">
              {title}
            </p>
          </div>
        )}
        <button
          type="button"
          onClick={() => goBack(exerciseId)}
          aria-label="Retour"
          className="native:top-[calc(env(safe-area-inset-top)+0.75rem)] absolute left-3 top-3 flex h-11 w-11 items-center justify-center rounded-full bg-black/35 text-white shadow-lg ring-1 ring-white/40 backdrop-blur-md transition hover:bg-black/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white active:scale-90"
        >
          <ArrowLeft className="h-5 w-5" strokeWidth={2.5} aria-hidden />
        </button>
        {elapsedLabel !== undefined && (
          <div
            className="text-ink-900 native:top-[calc(env(safe-area-inset-top)+0.75rem)] absolute left-1/2 top-3 flex h-11 -translate-x-1/2 items-center gap-1.5 rounded-full bg-white/90 px-3 font-mono text-sm font-bold tabular-nums shadow-md backdrop-blur-sm"
            aria-label={`Temps écoulé : ${elapsedLabel}`}
          >
            <span aria-hidden="true">⏱</span>
            {elapsedLabel}
          </div>
        )}
        {progress !== undefined && (
          <div className="absolute inset-x-0 bottom-0 px-4 pb-3">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/70 shadow-sm backdrop-blur-sm">
              <div
                className="bg-primary-400 h-full rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

/** Récompense d'une réponse : popup (rendu classique) ou note du prof (sur la feuille). */
interface RewardNote {
  value: XpReward;
  wasCorrect: boolean;
  explanationMd?: string;
  errorFeedbackMd?: string;
  /** Question répondue (statement_question) : cartes SKILLCARDS reliées. */
  questionId?: string | null;
}

/** Fin d'une démo sur la feuille : temps du tampon « 100 % » avant l'écran d'inscription. */
const DEMO_STAMP_DELAY_MS = 3000;

/** Le bas de la feuille au bilan : se montre à l'apparition (la note est en bas). */
function PaperFinalGrade({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    ref.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  }, []);
  return (
    <div ref={ref} className="pb-safe pb-10" style={{ scrollMarginTop: '5rem' }}>
      {children}
    </div>
  );
}

/** La correction du prof sous une réponse (mode « sur la feuille »). */
function SheetTeacherNote({
  note,
  past = false,
  cards,
  userId,
}: {
  note: RewardNote;
  past?: boolean;
  cards: ReturnType<typeof cardsForQuestion>;
  userId: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  // la note apparaît sous la feuille : on la montre, juste au-dessus du dock
  useEffect(() => {
    if (!past) ref.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [past]);
  return (
    <div ref={ref} style={{ scrollMarginBottom: 'calc(var(--dock-h, 0px) + 1rem)' }}>
      <TeacherNote
        wasCorrect={note.wasCorrect}
        streak={note.value.streak}
        gain={note.value.total}
        streakBonus={note.value.streakBonus}
        timeBonus={note.value.timeBonus}
        explanationMd={note.explanationMd}
        errorFeedbackMd={note.errorFeedbackMd}
        past={past}
        extra={
          note.wasCorrect && !past && cards.length > 0 ? (
            <CardXpGain cards={cards} userId={userId} />
          ) : undefined
        }
      />
    </div>
  );
}

// ─── Écran de fin (bilan) ─────────────────────────────────────────────────────
// Cover, une ligne « bonnes réponses · XP · avatar · HORA · temps », gros score
// %, message, un seul bouton « Continuer » et le corrigé repliable. La tentative
// est VALIDÉE AUTOMATIQUEMENT (`finish_attempt`) après `RESULT_AUTO_VALIDATE_MS` :
// XP et HORA comptent alors depuis 0. Le score local s'affiche tout de suite ;
// le score serveur le remplace après validation.

/** Délai avant la validation automatique du bilan (le temps de voir le score). */
export const RESULT_AUTO_VALIDATE_MS = 1500;

export function ResultScreen({
  exercise,
  blocks,
  correctCount,
  totalCheckpoints,
  elapsedMs,
  previewMode,
  serverScore,
  xpAwarded,
  sessionXp,
  isValidating,
  isValidated,
  validateError = false,
  autoValidateMs = RESULT_AUTO_VALIDATE_MS,
  onValidate,
  onExit,
  cardGains = [],
  footer,
  continueHidden = false,
  coverOverride,
  paper = false,
}: {
  exercise: PlayerExercise;
  blocks: ExerciseBlock[];
  correctCount: number;
  totalCheckpoints: number;
  elapsedMs: number;
  previewMode: boolean;
  serverScore: number | null;
  xpAwarded: number | null;
  sessionXp: number;
  isValidating: boolean;
  isValidated: boolean;
  /** La validation automatique a échoué : bouton « Réessayer ». */
  validateError?: boolean;
  /** Délai avant la validation automatique (XP + HORA crédités, sans appui). */
  autoValidateMs?: number;
  onValidate: () => void;
  onExit: () => void;
  /** XP gagnée par chaque SKILLCARD reliée sur cette tentative (migration 0279). */
  cardGains?: CardXpGainItem[];
  /** Sous le bilan (ex. offre SKILLCARD après les questions gratuites). */
  footer?: ReactNode;
  /** la vidéo du défi streamé attend un choix (publier ou annuler) : « Continuer » reste caché */
  continueHidden?: boolean;
  /** Remplace l'illustration du haut (ex. appel à débloquer la SKILLCARD, visible sans défiler). */
  coverOverride?: ReactNode;
  /**
   * Mode « sur la feuille » : pas d'écran à part, la note du prof (sur 20, à
   * l'encre rouge) s'écrit en bas de la feuille, sous les questions corrigées.
   */
  paper?: boolean;
}) {
  const { data: cover, isLoading: coverLoading } = useExerciseCover({
    exerciseId: exercise.id,
    chapterId: exercise.chapter_id,
    fallback: exercise.illustration_url,
  });
  // Pas d'illustration → cover de la SKILLCARD principale de l'exo, comme en
  // début d'exercice (même requête, déjà en cache).
  // Liens chargés dans tous les cas : la carte principale donne aussi la teinte
  // de l'aura de récompense (même requête que le début d'exercice, en cache).
  const { data: cardLinks } = useExerciseCardLinks(exercise.id);
  const mainCard = cardLinks ? mainCardOfLinks(cardLinks) : null;
  const skillcard = !coverLoading && !cover ? mainCard : null;
  const localScore = totalCheckpoints > 0 ? Math.round((correctCount / totalCheckpoints) * 100) : 0;
  const score = serverScore ?? localScore;
  const passed = score >= 60;
  // XP à afficher : en mode réel validé, l'XP RÉELLEMENT créditée par
  // `finish_attempt` ; en mode test/aperçu (jamais validé), l'XP indicative
  // accumulée pendant la partie (`sessionXp` = base + bonus de série).
  const xpToShow = isValidated ? (xpAwarded ?? 0) : previewMode ? sessionXp : 0;
  // HORA de la tentative : somme des SKILLCARDS reliées, affichée une fois créditée.
  const horaTotal = cardGains.reduce((sum, g) => sum + Math.max(0, g.gain), 0);
  const horaToShow = isValidated ? horaTotal : 0;
  // XP : toujours visible (compte depuis 0) sauf si rien n'a été gagné au final.
  const showXp = previewMode ? sessionXp > 0 : !(isValidated && (xpAwarded ?? 0) === 0);
  const showHora = cardGains.length > 0;

  // Validation AUTOMATIQUE (une fois, après `autoValidateMs`) : l'élève n'appuie
  // plus que sur « Continuer ». Un appui avant la fin valide puis sort.
  const autoFired = useRef(false);
  const validateRef = useRef(onValidate);
  validateRef.current = onValidate;
  const [exitAfter, setExitAfter] = useState(false);
  useEffect(() => {
    if (previewMode || isValidated || isValidating || autoFired.current) return;
    const t = window.setTimeout(() => {
      if (autoFired.current) return; // déjà lancée par « Continuer »
      autoFired.current = true;
      validateRef.current();
    }, autoValidateMs);
    return () => window.clearTimeout(t);
  }, [previewMode, isValidated, isValidating, autoValidateMs]);
  useEffect(() => {
    if (exitAfter && isValidated) onExit();
  }, [exitAfter, isValidated, onExit]);
  const handleContinue = () => {
    if (previewMode || isValidated) return onExit();
    setExitAfter(true);
    if (!isValidating) {
      autoFired.current = true;
      onValidate();
    }
  };

  const continueButton = continueHidden ? (
    <p className="text-ink-600 text-center font-sans text-sm" data-testid="continue-hidden">
      Publie ou annule ta vidéo pour continuer.
    </p>
  ) : previewMode ? (
    <Button
      variant="gold"
      size="lg"
      className="w-full rounded-full sm:w-auto sm:px-12"
      onClick={onExit}
    >
      Quitter le mode test
    </Button>
  ) : validateError && !isValidating && !isValidated ? (
    <Button
      variant="gold"
      size="lg"
      className="w-full rounded-full sm:w-auto sm:px-12"
      onClick={() => {
        autoFired.current = true;
        onValidate();
      }}
    >
      Réessayer
    </Button>
  ) : (
    <Button
      variant="gold"
      size="lg"
      className="w-full rounded-full sm:w-auto sm:px-12"
      onClick={handleContinue}
      disabled={exitAfter && !isValidated}
    >
      {exitAfter && !isValidated ? 'Enregistrement…' : 'Continuer'}
    </Button>
  );

  if (paper) {
    // note sur 20 au demi-point, d'après le score (serveur s'il est là)
    const grade = Math.round((score / 100) * 40) / 2;
    return (
      <PaperFinalGrade>
        <FinalTeacherGrade
          correct={correctCount}
          total={totalCheckpoints}
          grade={grade}
          remark={teacherAppreciation(grade)}
        >
          <ul
            className="pp-teacher-small space-y-0.5 font-[inherit]"
            data-testid="result-paper-details"
          >
            {showXp && (
              <li data-testid="result-xp">
                +<CountUpNumber value={xpToShow} duration={1100} /> XP
              </li>
            )}
            {showHora && (
              <li data-testid="result-hora">
                +<CountUpNumber value={horaToShow} duration={1100} /> HORA
              </li>
            )}
            <li>Temps : {formatTimer(elapsedMs)}</li>
          </ul>
        </FinalTeacherGrade>
        {cardGains.length > 1 && (
          <div className="mt-2 w-full max-w-sm space-y-2">
            <CardXpGainList items={cardGains} />
          </div>
        )}
        {coverOverride && <div className="mt-4">{coverOverride}</div>}
        <div className="mt-4 flex flex-col items-stretch gap-2 sm:items-start">
          {validateError && !isValidating && !isValidated && (
            <p className="text-accent-600 font-sans text-sm">
              Tes points n’ont pas pu être enregistrés.
            </p>
          )}
          {continueButton}
        </div>
        {footer && <div className="mt-6">{footer}</div>}
      </PaperFinalGrade>
    );
  }

  const statTile =
    'border-ink-100 w-[4.25rem] shrink-0 rounded-2xl border bg-white px-1 py-2.5 sm:w-24 sm:px-2 sm:py-3';
  const statValue = 'font-display text-ink-900 text-base font-extrabold tabular-nums sm:text-xl';
  const statLabel = 'text-ink-400 font-sans text-[11px] leading-tight sm:text-xs';

  return (
    <div className="pb-safe mx-auto flex min-h-[80vh] max-w-2xl flex-col items-stretch justify-center p-4 lg:max-w-4xl">
      {coverOverride ?? (
        <div className="animate-scale-in border-ink-100 from-primary-50 bg-linear-to-br to-ink-50 relative aspect-video w-full overflow-hidden rounded-2xl border shadow-sm">
          {cover && (
            <img
              src={cover}
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 h-full w-full object-cover"
            />
          )}
          {skillcard && <SkillcardExerciseCover card={skillcard} exerciseTitle={exercise.title} />}
          <div
            aria-hidden="true"
            className="bg-linear-to-t from-ink-50 pointer-events-none absolute inset-x-0 bottom-0 h-1/2 to-transparent"
          />
        </div>
      )}

      <div className="mt-5 space-y-4 text-center">
        <div
          className="animate-scale-in space-y-1"
          style={{ animationDelay: '260ms', animationFillMode: 'both' }}
        >
          {/* Une seule ligne : bonnes réponses · XP · avatar (aura) · HORA · temps.
              XP et HORA comptent depuis 0 dès qu'ils sont crédités. */}
          <div
            data-testid="result-aura-row"
            className="flex items-center justify-center gap-1.5 sm:gap-3"
          >
            {totalCheckpoints > 0 ? (
              <div className={statTile}>
                <p className={statValue}>
                  <CountUpNumber value={correctCount} delay={360} duration={900} />/
                  {totalCheckpoints}
                </p>
                <p className={statLabel}>bonnes réponses</p>
              </div>
            ) : (
              <div className="w-[4.25rem] shrink-0 sm:w-24" aria-hidden="true" />
            )}
            {showXp && (
              <div
                data-testid="result-xp"
                className="border-xp-200 bg-xp-50 w-[4.25rem] shrink-0 rounded-2xl border px-1 py-2.5 sm:w-24 sm:px-2 sm:py-3"
              >
                <p className="font-display text-xp-700 text-base font-extrabold tabular-nums sm:text-xl">
                  +<CountUpNumber value={xpToShow} duration={1100} />
                </p>
                <p className="text-xp-600 font-sans text-[11px] sm:text-xs">XP</p>
              </div>
            )}
            <ResultAura score={score} card={mainCard} />
            {showHora && (
              <div
                data-testid="result-hora"
                className="ocean:border-[#5b3a8c] ocean:bg-[#2a1748] w-[4.25rem] shrink-0 rounded-2xl border border-[#e9d8ff] bg-[#f6efff] px-1 py-2.5 sm:w-24 sm:px-2 sm:py-3"
              >
                <p className="font-display ocean:text-[#d9b8ff] text-base font-extrabold tabular-nums text-[#7a3fd1] sm:text-xl">
                  +<CountUpNumber value={horaToShow} duration={1100} />
                </p>
                <p className="ocean:text-[#c9a6f5] font-sans text-[11px] text-[#8b5cc9] sm:text-xs">
                  HORA
                </p>
              </div>
            )}
            <div className={statTile}>
              <p className={statValue}>{formatTimer(elapsedMs)}</p>
              <p className={statLabel}>temps</p>
            </div>
          </div>
          <h1 className="font-display text-ink-900 pt-2 text-5xl font-extrabold leading-none">
            <CountUpNumber value={score} delay={260} />%
          </h1>
          <p className="font-display text-ink-900 text-lg font-bold">
            {passed ? 'Exercice réussi !' : 'Continue comme ça !'}
          </p>
          <p className="text-ink-500 font-sans text-sm">
            <MathText>{exercise.title}</MathText>
          </p>
        </div>

        {/* Plusieurs SKILLCARDS : le détail par carte (une seule : déjà dans la ligne). */}
        {cardGains.length > 1 && (
          <div
            className="animate-slide-up mx-auto w-full max-w-sm space-y-2 text-left"
            style={{ animationDelay: '410ms', animationFillMode: 'both' }}
          >
            <CardXpGainList items={cardGains} />
          </div>
        )}

        <div
          className="animate-slide-up flex flex-col items-center gap-2"
          style={{ animationDelay: '460ms', animationFillMode: 'both' }}
        >
          {validateError && !isValidating && !isValidated && (
            <p className="text-accent-600 font-sans text-sm">
              Tes points n’ont pas pu être enregistrés.
            </p>
          )}
          {continueButton}
        </div>

        <details className="mt-2 text-left">
          <summary className="text-ink-700 cursor-pointer text-center text-sm font-semibold">
            Voir le corrigé complet
          </summary>
          <CorrectionRecap blocks={blocks} />
        </details>
      </div>
      {footer && <div className="mt-6">{footer}</div>}
    </div>
  );
}

// Didacticiel du brouillon (exercice guidé de la démo) : chargé seulement la première fois.
const GuidedTutorial = lazy(() => import('@/features/scratch-tutorial/guided-tutorial'));

// ─── Composant principal ──────────────────────────────────────────────────────

/**
 * La TROUSSE est rendue au niveau de l'écran, la feuille vit dans le dock : ce provider est le canal
 * par lequel la feuille y met ses outils (tableau à dresser, arbre, avancement).
 */
export function ExercisePlayerV2(props: Parameters<typeof ExercisePlayerV2Inner>[0]) {
  return (
    <TrousseToolsProvider>
      <ExercisePlayerV2Inner {...props} />
    </TrousseToolsProvider>
  );
}

function ExercisePlayerV2Inner({
  exerciseId,
  previewMode = false,
  onExitPreview,
  demoMode = false,
  demoPayload,
  onDemoComplete,
}: ExercisePlayerV2Props) {
  const noWrite = previewMode || demoMode;
  const { user, profile: authProfile } = useAuth();
  // Premier exercice joué « sur la feuille » : la prise en main du brouillon (exercice guidé) s'intercale entre
  // « Commencer » et la 1re question, une fois par appareil. « Passer » démarre l'exercice tout de suite.
  const paperMode = usePaperPlayer(null);
  const [tutorial, setTutorial] = useState(false);
  const myStudent = useStudentProfile(authProfile?.role === 'student' ? user?.id : undefined);
  const routeParams = useParams({ strict: false }) as {
    subject?: string;
    level?: string;
    chapter?: string;
  };

  // Flag « calculatrice requise » — lecture directe (non sensible, hors RPC).
  const { data: requiresCalculator = false } = useQuery({
    queryKey: ['exercise-requires-calculator', exerciseId],
    enabled: !!exerciseId && !demoMode,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<boolean> => {
      const { data } = await supabase
        .from('exercises')
        .select('requires_calculator')
        .eq('id', exerciseId)
        .maybeSingle();
      return (data as { requires_calculator?: boolean } | null)?.requires_calculator ?? false;
    },
  });

  // Lu par la queryFn : un state serait capturé périmé dans sa closure.
  const attemptIdRef = useRef<string | null>(null);
  const [isPreparing, setIsPreparing] = useState(false);

  const query = useQuery({
    queryKey: [
      'exercise-v2-player',
      exerciseId,
      demoMode ? 'demo' : previewMode ? 'preview' : 'student',
    ],
    enabled: !demoMode || demoPayload == null,
    initialData: demoMode ? demoPayload : undefined,
    queryFn: async (): Promise<PlayerPayload> => {
      if (demoMode) {
        // Si on arrive ici sans `demoPayload`, on n'a aucun moyen de récupérer
        // l'aperçu côté player (le loader anon V2 vit côté route). On renvoie
        // une erreur explicite plutôt que de faire un appel authentifié.
        return { error: 'demo_payload_required' };
      }
      if (previewMode) {
        const { data, error } = await supabase.functions.invoke<ExerciseV2Payload>(
          'exercise-get-for-edit',
          { body: { exercise_id: exerciseId } },
        );
        if (error) throw error;
        if (!data) return { error: 'empty_response' };
        return {
          exercise: {
            id: data.meta.id,
            chapter_id: data.meta.chapter_id,
            title: data.meta.title,
            difficulty: data.meta.difficulty,
            estimated_minutes: data.meta.estimated_minutes,
            illustration_url: data.meta.illustration_url,
            content_version: data.meta.content_version,
          },
          blocks: data.blocks,
          access: { allowed: true },
          attempt_id: null,
        };
      }
      // Surcharge 2-args (migration 0229) : sur un exercice PARAMÉTRIQUE
      // (Blueprint), la RPC applique le calque de l'instance de la tentative —
      // les valeurs des paramètres, et donc tout le corrigé qui en dérive,
      // sont celles de CETTE tentative. `p_attempt_id` est explicite pour que
      // l'écran de bilan (tentative terminée) réaffiche les bonnes valeurs ;
      // null au premier chargement, la RPC résout alors elle-même.
      const { data, error } = await (supabase.rpc as unknown as UntypedRpc)(
        'get_exercise_v2_for_student',
        { p_exercise_id: exerciseId, p_attempt_id: attemptIdRef.current },
      );
      if (error) throw error;
      return (data ?? {}) as PlayerPayload;
    },
    staleTime: 30 * 1000,
  });

  const startAttempt = useMutation({
    mutationFn: async (): Promise<string> => {
      if (noWrite) return demoMode ? 'demo' : 'preview';
      const { data, error } = await (supabase.rpc as unknown as UntypedRpc)('start_attempt', {
        p_exercise_id: exerciseId,
      });
      if (error) throw error;
      // Refus serveur (déjà fait aujourd'hui, 0660…) : jamais pris pour un id.
      const res = parseStartAttempt(data);
      if (!res.ok) throw new StartAttemptError(res.error, res.availableAt);
      return res.attemptId;
    },
  });
  const [startBlocked, setStartBlocked] = useState<string | null>(null);

  const qc = useQueryClient();
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [phase, setPhase] = useState<'intro' | 'exercise'>('intro');

  useEffect(() => {
    if (query.data?.attempt_id) {
      attemptIdRef.current = query.data.attempt_id;
      setAttemptId(query.data.attempt_id);
    }
  }, [query.data?.attempt_id]);

  // Retour de Stripe après un achat (pass, abonnement ou SKILLCARD) : on
  // recharge l'exercice (la RPC le renvoie complet) PUIS on reprend À LA
  // QUESTION SUIVANTE — tentative mémorisée avant le paiement (réponses relues
  // en base) ou nouvelle tentative si elle avait été validée. Jamais de retour
  // à la question 1. Fenêtre « SKILLCARD DÉBLOQUÉE ! » à l'arrivée.
  const [resume, setResume] = useState<
    | { status: 'pending'; info: ExerciseResume; since: number }
    | {
        status: 'ready';
        stepIdx: number;
        answers: Record<string, AnswerRecord>;
        /** Retour de paiement : fenêtre « SKILLCARD DÉBLOQUÉE ! ». */
        celebrate: boolean;
        /** Tout est répondu : on rouvre directement le bilan. */
        atEnd?: boolean;
        elapsedMs?: number;
      }
    | null
  >(null);
  useEffect(() => {
    if (noWrite || !exerciseId) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('pass_success') !== '1') return;
    window.history.replaceState({}, '', window.location.pathname);
    const info = takeResume(exerciseId);
    // Rechargement lancé AVANT d'afficher « Déblocage… » : la reprise attend la
    // fin de ce rechargement (`!query.isFetching`), contenu complet compris.
    void qc.invalidateQueries({ queryKey: ['exercise-v2-player', exerciseId] });
    if (info) setResume({ status: 'pending', info, since: Date.now() });
  }, [exerciseId, qc, noWrite]);

  const payload = query.data;
  const blocks = useMemo(() => payload?.blocks ?? [], [payload?.blocks]);

  // Section `statement` triée par position. Le premier bloc `statement`
  // (énoncé général) devient la situation ; le reste = les étapes.
  const statementSection = useMemo(() => blocksInSection(blocks, 'statement'), [blocks]);
  const situationBlock = useMemo(
    () => statementSection.find((b) => b.block_type === 'statement') ?? null,
    [statementSection],
  );
  const stepBlocks = useMemo(
    () =>
      statementSection.filter((b) => {
        if (b.id === situationBlock?.id) return false;
        // Un clue_hunt AVEC parent_id est rendu DANS la carte de sa question
        // parente, pas comme un bloc autonome dans la liste. Sans parent_id
        // (racine), il reste un bloc autonome dans le flow.
        if (b.block_type === 'clue_hunt' && b.parent_id !== null) return false;
        return true;
      }),
    [statementSection, situationBlock?.id],
  );

  const resumeInfo = resume?.status === 'pending' ? resume.info : null;
  // Données rechargées APRÈS le retour (exercice complet) : jamais le cache tronqué.
  const resumeReady =
    resume?.status === 'pending' &&
    !!query.data?.blocks &&
    !query.isFetching &&
    query.dataUpdatedAt >= resume.since;
  useEffect(() => {
    if (!resumeInfo || !resumeReady) return;
    let cancelled = false;
    void (async () => {
      let id = resumeInfo.attemptId;
      let answers: Record<string, AnswerRecord> = {};
      if (id) {
        const [{ data: att }, { data: rows }] = await Promise.all([
          supabase.from('attempts').select('status').eq('id', id).maybeSingle(),
          supabase
            .from('attempt_steps')
            .select('block_id, chosen_block_id, chosen_block_ids, was_correct')
            .eq('attempt_id', id),
        ]);
        if ((att as { status?: string } | null)?.status === 'in_progress') {
          answers = answersFromAttemptSteps(
            (rows ?? []) as unknown as Parameters<typeof answersFromAttemptSteps>[0],
          );
        } else {
          id = null;
        }
      }
      if (!id) id = await startAttempt.mutateAsync();
      if (cancelled) return;
      const steps = buildPlaySteps(stepBlocks, blocks).map((st) => ({
        checkpointId: st.checkpoint?.id ?? null,
      }));
      const stepIdx = resumeStepIndex(steps, new Set(Object.keys(answers)), resumeInfo.stepIdx);
      attemptIdRef.current = id;
      setAttemptId(id);
      setPhase('exercise');
      setResume({ status: 'ready', stepIdx, answers, celebrate: true });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- une seule résolution par retour de paiement
  }, [resumeInfo, resumeReady]);

  // Rechargement de la page (ou retour sur l'exercice) avec une tentative EN
  // COURS déjà commencée : on la reprend telle quelle — réponses relues en base,
  // à la 1re question sans réponse, ou directement le bilan si tout est répondu
  // mais l'XP pas encore validée. Jamais de retour à la question 1.
  const autoResumeId =
    !noWrite && resume === null && phase === 'intro' ? (query.data?.attempt_id ?? null) : null;
  const [autoResumeDone, setAutoResumeDone] = useState<string | null>(null);
  const autoResumePending =
    !!autoResumeId && autoResumeDone !== autoResumeId && !!query.data?.blocks;
  useEffect(() => {
    if (!autoResumePending || !autoResumeId) return;
    let cancelled = false;
    void (async () => {
      const [{ data: att }, { data: rows }] = await Promise.all([
        supabase.from('attempts').select('status').eq('id', autoResumeId).maybeSingle(),
        supabase
          .from('attempt_steps')
          .select('block_id, chosen_block_id, chosen_block_ids, was_correct, time_ms')
          .eq('attempt_id', autoResumeId),
      ]);
      if (cancelled) return;
      const inProgress = (att as { status?: string } | null)?.status === 'in_progress';
      const list = (rows ?? []) as unknown as (Parameters<
        typeof answersFromAttemptSteps
      >[0][number] & {
        time_ms?: number | null;
      })[];
      const answers = inProgress ? answersFromAttemptSteps(list) : {};
      const steps = buildPlaySteps(stepBlocks, blocks).map((st) => ({
        checkpointId: st.checkpoint?.id ?? null,
      }));
      const plan = planAutoResume(steps, new Set(Object.keys(answers)));
      if (plan) {
        attemptIdRef.current = autoResumeId;
        setAttemptId(autoResumeId);
        setResume({
          status: 'ready',
          stepIdx: plan.stepIdx,
          answers,
          celebrate: false,
          atEnd: plan.atEnd,
          elapsedMs: elapsedFromAttemptSteps(list),
        });
        setPhase('exercise');
      }
      setAutoResumeDone(autoResumeId);
    })().catch(() => {
      if (!cancelled) setAutoResumeDone(autoResumeId);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- une seule vérification par tentative
  }, [autoResumePending, autoResumeId]);

  if (query.isPending) {
    return <PageLoader label="Ouverture de l'exercice…" />;
  }
  if (autoResumePending) {
    return <PageLoader label="Reprise de ton exercice…" />;
  }
  if (resume?.status === 'pending') {
    return <PageLoader label="Déblocage de la suite…" />;
  }
  if (query.isError) {
    return <div className="p-6 text-center text-sm text-red-600">Erreur de chargement.</div>;
  }
  if (!payload || payload.error || !payload.exercise) {
    return (
      <div className="p-6 text-center text-sm text-amber-700">
        Exercice indisponible ({payload?.error ?? 'empty'}).
      </div>
    );
  }

  const ex = payload.exercise;
  const totalQuestions = stepBlocks.filter(
    (b) => b.block_type === 'statement_question' && orderedCheckpointsFor(blocks, b.id).length > 0,
  ).length;

  // Partie payante d'un exercice de SKILLCARD découpé, sans accès (migration
  // 0501) : aucune question gratuite → paywall à la place de l'écran d'accueil,
  // sans créer de tentative.
  if (!noWrite && phase === 'intro' && isLockedUpfront(payload.access)) {
    return (
      <LockedExerciseScreen
        exercise={ex}
        access={payload.access}
        totalQuestions={payload.access?.total_questions ?? totalQuestions}
        onUnlocked={() => {
          void qc.invalidateQueries({ queryKey: ['exercise-v2-player', exerciseId] });
          void qc.invalidateQueries({ queryKey: ['scratch-models', exerciseId] });
        }}
      />
    );
  }

  // Démarre la tentative puis la 1re question (après « Commencer », ou après le didacticiel).
  async function beginExercise() {
    let id: string;
    try {
      id = attemptId ?? (await startAttempt.mutateAsync());
    } catch (e) {
      setStartBlocked(
        startAttemptErrorMessage(e instanceof StartAttemptError ? e.code : 'UNKNOWN'),
      );
      return;
    }
    attemptIdRef.current = id;
    setAttemptId(id);
    // Exercice paramétrique : c'est `start_attempt` qui FIXE les valeurs
    // des paramètres de cette tentative. Sans ce rechargement, l'élève
    // jouerait le rendu canonique affiché à l'écran d'intro alors que
    // le serveur note sur l'instance de sa tentative.
    if (!noWrite) {
      setIsPreparing(true);
      try {
        await qc.invalidateQueries({ queryKey: ['exercise-v2-player', exerciseId] });
      } finally {
        setIsPreparing(false);
      }
    }
    setPhase('exercise');
  }

  if (tutorial) {
    return (
      <Suspense fallback={<PageLoader label="Préparation de ta feuille…" />}>
        <GuidedTutorial
          level={routeParams.level || myStudent.data?.level || null}
          avoidExerciseId={exerciseId}
          context="exercice"
          intro="Avant ton premier exercice."
          doneLabel="Commencer l’exercice"
          onDone={() => {
            setTutorial(false);
            void beginExercise();
          }}
        />
      </Suspense>
    );
  }

  if (phase === 'intro' || !attemptId) {
    return (
      <>
        {previewMode && <PreviewBanner onExit={onExitPreview} />}
        <ExerciseHeader
          exerciseId={ex.id}
          chapterId={ex.chapter_id}
          illustrationUrl={ex.illustration_url}
          title={ex.title}
        />
        <IntroScreen
          exercise={ex}
          situationBlock={isTitleEcho(situationBlock, ex.title) ? null : situationBlock}
          totalQuestions={totalQuestions}
          totalSteps={stepBlocks.length}
          isStarting={startAttempt.isPending || isPreparing}
          blockedMessage={startBlocked}
          challenge={!noWrite}
          onStart={async () => {
            if (!noWrite && paperMode && isFirstScratchUse()) {
              setTutorial(true);
              return;
            }
            await beginExercise();
          }}
        />
      </>
    );
  }

  return (
    <PlayerSession
      exercise={ex}
      blocks={blocks}
      situationBlock={isTitleEcho(situationBlock, ex.title) ? null : situationBlock}
      stepBlocks={stepBlocks}
      attemptId={attemptId}
      access={payload.access}
      previewMode={previewMode}
      onExitPreview={onExitPreview}
      demoMode={demoMode}
      onDemoComplete={onDemoComplete}
      requiresCalculator={requiresCalculator}
      userId={user?.id ?? ''}
      subject={routeParams.subject ?? ''}
      level={routeParams.level ?? ''}
      chapter={routeParams.chapter ?? ''}
      initialStepIdx={resume?.status === 'ready' ? resume.stepIdx : 0}
      initialAnswers={resume?.status === 'ready' ? resume.answers : undefined}
      celebrateUnlock={resume?.status === 'ready' && resume.celebrate}
      resumeAtEnd={resume?.status === 'ready' && !!resume.atEnd}
      initialElapsedMs={resume?.status === 'ready' ? (resume.elapsedMs ?? 0) : 0}
    />
  );
}

// ─── Bandeau « Mode test » ────────────────────────────────────────────────────

function PreviewBanner({ onExit }: { onExit?: () => void }) {
  return (
    <div className="border-primary-200 bg-primary-50 mx-auto mb-2 mt-2 flex max-w-2xl items-center gap-2 rounded-xl border px-3 py-2 lg:max-w-4xl">
      <span className="text-primary-700 font-display flex-1 text-xs font-bold uppercase tracking-wide">
        🎬 Mode test — aucun résultat enregistré
      </span>
      <button
        onClick={() => {
          if (onExit) onExit();
          else window.history.back();
        }}
        className="text-primary-700 hover:text-primary-900 font-display rounded-lg px-2 py-1 text-xs font-bold transition-colors"
      >
        Quitter
      </button>
    </div>
  );
}

// ─── Partie payante verrouillée (migration 0501) ──────────────────────────────

function LockedExerciseScreen({
  exercise,
  access,
  totalQuestions,
  onUnlocked,
}: {
  exercise: PlayerExercise;
  access: V2Access | undefined;
  totalQuestions: number;
  onUnlocked: () => void;
}) {
  return (
    <>
      <ExerciseHeader
        exerciseId={exercise.id}
        chapterId={exercise.chapter_id}
        illustrationUrl={exercise.illustration_url}
        title={exercise.title}
      />
      <div className="pb-safe mx-auto max-w-2xl space-y-4 p-4 lg:max-w-4xl">
        <div
          className="border-ink-200 rounded-2xl border bg-white p-5"
          data-testid="locked-exercise"
        >
          <h1 className="font-display text-ink-900 text-xl font-extrabold leading-tight">
            <MathText>{exercise.title}</MathText>
          </h1>
          <p className="text-ink-500 mt-1 flex items-center gap-1.5 font-sans text-sm">
            <Lock className="h-4 w-4 shrink-0" aria-hidden />
            {totalQuestions} question{totalQuestions > 1 ? 's' : ''} à débloquer
          </p>
        </div>
        {isSkillcardPaywall(access) ? (
          <SkillcardPaywallCard
            cardId={access?.skillcard_id ?? ''}
            freeSample={false}
            freeQuestions={0}
            exerciseId={exercise.id}
            attemptId={null}
            onUnlocked={onUnlocked}
          />
        ) : (
          <ExercisePaywallCard
            chapterType={access?.chapter_type === 'epreuves' ? 'epreuves' : 'normal'}
            exerciseId={exercise.id}
            attemptId={null}
            onUnlocked={onUnlocked}
          />
        )}
        <button
          type="button"
          onClick={() => goBack(exercise.id)}
          className="text-ink-500 hover:text-ink-700 mx-auto block min-h-11 font-sans text-sm font-medium"
        >
          Plus tard
        </button>
      </div>
    </>
  );
}

// ─── Écran d'intro ────────────────────────────────────────────────────────────

function IntroScreen({
  exercise,
  situationBlock,
  totalQuestions,
  totalSteps,
  isStarting,
  onStart,
  blockedMessage = null,
  level,
  challenge = false,
}: {
  exercise: PlayerExercise;
  situationBlock: ExerciseBlock | null;
  totalQuestions: number;
  totalSteps: number;
  isStarting: boolean;
  onStart: () => void;
  /** Démarrage refusé (déjà fait aujourd'hui…) : affiché à la place du bouton. */
  blockedMessage?: string | null;
  /** niveau de l'exercice (version des outils proposés) */
  level?: string | null;
  /** Propose « En faire un défi privé » (0722) : jamais en aperçu ni en démo. */
  challenge?: boolean;
}) {
  const diff = DIFF_META[exercise.difficulty];
  return (
    <div className="pb-safe mx-auto max-w-2xl space-y-6 p-4 lg:max-w-4xl">
      <div className="border-ink-200 rounded-2xl border bg-white p-5">
        <div className="space-y-2">
          <div className="flex items-start gap-2">
            <h1 className="font-display text-ink-900 flex-1 text-xl font-extrabold leading-tight">
              <MathText>{exercise.title}</MathText>
            </h1>
            <span
              className={cn(
                'font-display mt-0.5 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase',
                diff.cls,
              )}
            >
              {diff.label}
            </span>
          </div>
          <p className="text-ink-400 font-sans text-xs">
            {totalSteps} étape{totalSteps > 1 ? 's' : ''}
            {totalQuestions > 0
              ? ` · ${totalQuestions} question${totalQuestions > 1 ? 's' : ''}`
              : ''}
            {exercise.estimated_minutes ? ` · ⏱ ${exercise.estimated_minutes} min` : ''}
          </p>
        </div>
      </div>

      {situationBlock && (
        <div className="border-primary-200 bg-primary-50/40 rounded-2xl border p-4 sm:p-5">
          <p className="font-display text-primary-600 mb-2 text-[11px] font-semibold uppercase tracking-widest">
            La situation
          </p>
          <StatementBlockBody block={situationBlock} />
        </div>
      )}

      {blockedMessage ? (
        <div
          role="status"
          data-testid="exercise-start-blocked"
          className="bg-xp-50 text-xp-800 ocean:bg-xp-500/15 ocean:text-xp-100 flex items-center gap-3 rounded-2xl p-4 font-sans text-sm"
        >
          <CheckCircle2 className="h-6 w-6 shrink-0" aria-hidden />
          <span>
            <span className="block font-semibold">Déjà fait aujourd’hui</span>
            {blockedMessage}
          </span>
        </div>
      ) : (
        // les deux boutons sur une ligne, au-dessus des révisions : on démarre sans défiler
        <div className="flex items-stretch gap-3">
          <button
            onClick={onStart}
            disabled={isStarting}
            className="bg-accent-400 font-display hover:bg-accent-500 inline-flex min-h-11 flex-[1.4] items-center justify-center gap-2 rounded-2xl px-4 py-4 text-base font-bold text-[#0a2e57] shadow-md transition-all active:scale-[.98] disabled:opacity-50"
          >
            {isStarting && <Spinner className="h-4 w-4" />}
            {isStarting ? 'Préparation…' : 'Commencer →'}
          </button>
          {challenge && (
            <div className="flex flex-1 [&>button]:h-full [&>button]:w-full">
              <ExerciseChallengeButton
                exercise={{
                  id: exercise.id,
                  title: exercise.title,
                  totalQuestions: totalQuestions,
                }}
              />
            </div>
          )}
        </div>
      )}

      {/* SKILLCARDS de l'exercice + outils qui leur sont reliés : réviser avant. */}
      <IntroRevision exerciseId={exercise.id} level={level} />
    </div>
  );
}

// ─── Rendu de bloc statement / statement_question ─────────────────────────────

/**
 * Consigne COURTE d'une étape de corrigé (`correction.prompt_md`), affichée
 * AVANT les choix du sous-QCM pour cadrer l'étape (ex. « Les coefficients
 * multiplicateurs sont : »). Vide si le bloc n'en porte pas.
 */
function stepPromptOf(checkpoint: ExerciseBlock | null): string {
  if (!checkpoint) return '';
  const p = (checkpoint.data as { prompt_md?: string }).prompt_md;
  return typeof p === 'string' ? p.trim() : '';
}

function StatementBlockBody({ block }: { block: ExerciseBlock }) {
  const visuals = visualsOf(block);
  if (block.block_type === 'clue_hunt') {
    return <ClueHuntCasesInline data={block.data as unknown as ClueHuntData} />;
  }
  if (block.block_type === 'statement') {
    const data = block.data as { content_md?: string };
    return (
      <div className="space-y-3">
        <div className="prose prose-sm text-ink-700 max-w-none text-sm">
          <MarkdownMath>{data.content_md ?? ''}</MarkdownMath>
        </div>
        {visuals.length > 0 && <VisualBlocksRenderer blocks={visuals} />}
      </div>
    );
  }
  if (block.block_type === 'statement_question') {
    const data = block.data as { prompt_md?: string; sub_statement_md?: string };
    return (
      <div className="space-y-2">
        {data.sub_statement_md && (
          <p className="text-ink-500 font-sans text-xs">
            <MarkdownMath>{data.sub_statement_md}</MarkdownMath>
          </p>
        )}
        {data.prompt_md && (
          <div className="text-ink-900 font-sans text-base leading-relaxed">
            <MarkdownMath>{airSentences(data.prompt_md)}</MarkdownMath>
          </div>
        )}
        {visuals.length > 0 && <VisualBlocksRenderer blocks={visuals} />}
      </div>
    );
  }
  return null;
}

/**
 * Variante de StatementBlockBody pour la situation : rend chaque visuel dans un
 * conteneur ancré (`v2-visual-<id>`) pour que la navigation rapide puisse scroller
 * vers un visuel précis (parité avec la barre d'icônes V1).
 */
function SituationBody({ block }: { block: ExerciseBlock }) {
  const visuals = visualsOf(block);
  const data = block.data as { content_md?: string };
  return (
    <div className="space-y-3">
      {data.content_md && (
        <div className="prose prose-sm text-ink-700 max-w-none text-sm">
          <MarkdownMath>{data.content_md}</MarkdownMath>
        </div>
      )}
      {visuals.map((v) => (
        <div key={v.id} id={`v2-visual-${v.id}`} style={{ scrollMarginTop: '4rem' }}>
          <VisualBlockRenderer block={v} />
        </div>
      ))}
    </div>
  );
}

// ─── Session de jeu ───────────────────────────────────────────────────────────

function PlayerSession({
  exercise,
  blocks,
  situationBlock,
  stepBlocks,
  attemptId,
  access,
  previewMode,
  onExitPreview,
  demoMode,
  onDemoComplete,
  requiresCalculator,
  userId,
  subject,
  level,
  chapter,
  initialStepIdx = 0,
  initialAnswers,
  celebrateUnlock = false,
  resumeAtEnd = false,
  initialElapsedMs = 0,
}: {
  exercise: PlayerExercise;
  blocks: ExerciseBlock[];
  situationBlock: ExerciseBlock | null;
  stepBlocks: ExerciseBlock[];
  attemptId: string;
  access?: V2Access;
  previewMode: boolean;
  onExitPreview?: () => void;
  demoMode: boolean;
  onDemoComplete?: () => void;
  requiresCalculator: boolean;
  userId: string;
  subject: string;
  level: string;
  chapter: string;
  /** Reprise après paiement : pas où reprendre (question suivante). */
  initialStepIdx?: number;
  /** Reprise après paiement : réponses déjà données (relues en base). */
  initialAnswers?: Record<string, AnswerRecord>;
  /** Reprise après paiement : fenêtre « SKILLCARD DÉBLOQUÉE ! ». */
  celebrateUnlock?: boolean;
  /** Reprise d'une tentative dont toutes les questions sont répondues → bilan. */
  resumeAtEnd?: boolean;
  /** Temps déjà passé sur la tentative reprise (chrono qui continue). */
  initialElapsedMs?: number;
}) {
  // Exercice « sur la feuille de brouillon » (drapeau `paper_player`, migration 0689).
  const { user: authUser, profile: authProfile } = useAuth();
  const paper = usePaperPlayer(
    authUser ? { id: authUser.id, role: authProfile?.role ?? null } : null,
  );
  const noWrite = previewMode || demoMode;
  const qc = useQueryClient();
  const [currentIdx, setCurrentIdx] = useState(initialStepIdx);
  // answers : id du bloc `correction` → réponse donnée.
  const [answers, setAnswers] = useState<Record<string, AnswerRecord>>(initialAnswers ?? {});
  const [unlockPopup, setUnlockPopup] = useState(celebrateUnlock);
  const [showResult, setShowResult] = useState(false);
  // Bilan : on remonte en haut — l'appel à débloquer (couverture) doit être vu
  // sans défiler, même sur un petit écran.
  useEffect(() => {
    // sur la feuille, la note finale est en bas : elle se montre elle-même
    if (showResult && !paper) window.scrollTo?.({ top: 0 });
  }, [showResult, paper]);
  const [showPaywall, setShowPaywall] = useState(false);
  // ─── Gamification feedback ───────────────────────────────────────────────
  // Série de bonnes réponses d'affilée (bonus XP croissant) + XP cumulé de la
  // session, affichés dans une popup centrée après chaque réponse. Purement
  // motivationnel — l'XP réellement créditée vient de `finish_attempt`.
  const [streak, setStreak] = useState(0);
  const [sessionXp, setSessionXp] = useState(0);
  const [reward, setReward] = useState<RewardNote | null>(null);
  // Mode « sur la feuille » : pas de popup, la correction du prof est écrite en
  // rouge sous chaque réponse (par checkpoint, gardée pour l'historique).
  const [notes, setNotes] = useState<Record<string, RewardNote>>({});
  // Démo « sur la feuille » : à la fin, le score tamponné par le prof, puis l'inscription.
  const [demoStamp, setDemoStamp] = useState<{ correct: number; total: number } | null>(null);
  const showReward = (checkpointId: string, note: RewardNote) => {
    if (paper) setNotes((prev) => ({ ...prev, [checkpointId]: note }));
    else setReward(note);
  };
  // SKILLCARDS reliées aux questions (popup « Bonne réponse », migration 0279).
  const cardLinks = useExerciseCardLinks(exercise.id, !noWrite);
  // XP des cartes AVANT validation : le plafond du bilan se calcule depuis cet
  // état figé (après `finish_attempt` la carte a déjà reçu l'XP).
  const myCards = useStudentSkillcards(
    !noWrite && (cardLinks.data?.length ?? 0) > 0 ? userId : undefined,
  );
  const [gainsBaseline, setGainsBaseline] = useState<{ id: string; xp: number }[] | null>(null);
  useEffect(() => {
    if (gainsBaseline === null && myCards.data) {
      setGainsBaseline(myCards.data.map((c) => ({ id: c.id, xp: c.xp })));
    }
  }, [gainsBaseline, myCards.data]);
  // Contenu verrouillé derrière le paywall (élève sans accès, questions au-delà
  // de la zone gratuite). En preview/démo, jamais de paywall.
  const locked = !noWrite && isExerciseLocked(access);
  // Exercice relié à une SKILLCARD sans accès : les questions gratuites
  // forment un exercice COMPLET — bilan + XP/HORA crédités (`finish_attempt`),
  // puis l'offre d'achat sous le bilan (au lieu du paywall qui gelait l'attempt).
  const skillcardSampleEnd = locked && endsWithSkillcardSample(access);
  const chapterType = access?.chapter_type === 'epreuves' ? 'epreuves' : 'normal';

  // ─── Chrono ─────────────────────────────────────────────────────────────
  // Démarre au montage de la session (entrée en phase « exercise »), s'incrémente
  // chaque seconde et se fige dès l'affichage du bilan (`showResult`). Sert à la
  // fois au badge d'en-tête et au temps affiché sur l'écran de fin.
  const startedAtRef = useRef(Date.now() - initialElapsedMs);
  const [elapsedMs, setElapsedMs] = useState(0);
  useEffect(() => {
    if (showResult || showPaywall) return;
    const tick = () => setElapsedMs(Date.now() - startedAtRef.current);
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [showResult, showPaywall]);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  // « Signaler cette question » (migration 0348) : question répondue visée.
  const [reportFor, setReportFor] = useState<string | null>(null);
  const canReport = !demoMode && !!userId;
  const seed = useMemo(() => hashString(attemptId), [attemptId]);

  // Auto-scroll en bas après réponse / Suivant — évite à l'utilisateur de scroller.
  // On scroll vers la CARTE D'ÉTAPE COURANTE (et non un sentinel en fin de
  // conteneur) avec `block: 'end'` + une `scroll-margin-bottom` qui réserve la
  // place du NextButton flottant : sans ça, le bouton fixe masque le bas de la
  // FeedbackBox quand elle apparaît, obligeant l'utilisateur à scroller à la
  // main. Le double rAF garantit que le scroll part APRÈS le commit de layout
  // (FeedbackBox montée, hauteur stabilisée).
  const currentStepRef = useRef<HTMLDivElement>(null);
  const scrollToCurrent = useCallback(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        currentStepRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      });
    });
  }, []);
  const answeredCount = Object.keys(answers).length;
  useEffect(() => {
    scrollToCurrent();
  }, [currentIdx, answeredCount, scrollToCurrent]);

  // Instant d'apparition de la question courante → temps de réflexion réel
  // (base du bonus de vitesse). Réinitialisé à chaque changement de question.
  const questionShownAtRef = useRef(Date.now());
  useEffect(() => {
    questionShownAtRef.current = Date.now();
  }, [currentIdx]);

  // Envoi d'une réponse : garde contre un 2e toucher (l'état `isPending` n'est
  // rendu qu'après coup) et message si la réponse n'est pas partie (réseau).
  const answeringRef = useRef(false);
  const [answerError, setAnswerError] = useState<string | null>(null);
  useEffect(() => {
    setAnswerError(null);
    answeringRef.current = false;
  }, [currentIdx]);

  const submit = useMutation({
    mutationFn: async (params: {
      correction_block_id: string;
      /** Ensemble coché (1 en mode single, ≥1 en mode multiple). */
      chosen_block_ids: string[];
      multiple: boolean;
      time_ms: number;
    }) => {
      if (noWrite) {
        // En preview/démo, on calcule la justesse localement (tout-ou-rien) :
        // l'ensemble coché doit égaler l'ensemble correct (correction +
        // distracteurs is_correct). Pas d'écriture en base.
        const cp = blocks.find((b) => b.id === params.correction_block_id);
        const correctIds = cp ? correctChoiceIds(blocks, cp) : [params.correction_block_id];
        return { was_correct: isAnswerSetCorrect(params.chosen_block_ids, correctIds) };
      }
      if (params.multiple) {
        const { data, error } = await (supabase.rpc as unknown as UntypedRpc)(
          'submit_block_answer_multi',
          {
            p_attempt_id: attemptId,
            p_correction_block_id: params.correction_block_id,
            p_chosen_block_ids: params.chosen_block_ids,
            p_time_ms: params.time_ms,
            p_hint_used: false,
          },
        );
        if (error) throw error;
        return data as { was_correct: boolean };
      }
      const { data, error } = await (supabase.rpc as unknown as UntypedRpc)('submit_block_answer', {
        p_attempt_id: attemptId,
        p_correction_block_id: params.correction_block_id,
        p_chosen_block_id: params.chosen_block_ids[0],
        p_time_ms: params.time_ms,
        p_hint_used: false,
      });
      if (error) throw error;
      return data as { was_correct: boolean; correct_block_id: string };
    },
  });

  // Mode de réponse "calculator" (générateur paramétrique Blueprint) : le
  // serveur (RPC submit_block_answer_numeric) est seul juge — relit
  // expected_value/tolerance depuis le bloc, jamais confiance en un booléen
  // calculé côté client (sauf en preview/démo, comme pour le QCM).
  const submitNumeric = useMutation({
    mutationFn: async (params: { correction_block_id: string; value: number; time_ms: number }) => {
      if (noWrite) {
        const cp = blocks.find((b) => b.id === params.correction_block_id);
        const calc = cp ? calculatorCheckpointData(cp) : null;
        const wasCorrect = calc
          ? Math.abs(params.value - calc.expectedValue) <= calc.tolerance
          : false;
        return { was_correct: wasCorrect };
      }
      const { data, error } = await (supabase.rpc as unknown as UntypedRpc)(
        'submit_block_answer_numeric',
        {
          p_attempt_id: attemptId,
          p_correction_block_id: params.correction_block_id,
          p_value: params.value,
          p_time_ms: params.time_ms,
          p_hint_used: false,
        },
      );
      if (error) throw error;
      return data as { was_correct: boolean; expected_value: number };
    },
  });

  const finishMutation = useMutation({
    mutationFn: async () => {
      if (noWrite) return { score: 0, xp_awarded: 0, passed: true };
      const { data, error } = await (supabase.rpc as unknown as UntypedRpc)('finish_attempt', {
        p_attempt_id: attemptId,
      });
      if (error) throw error;
      return data as { score?: number; xp_awarded?: number; passed?: boolean };
    },
    // Parité avec le player V1 : à la fin d'un exercice VRAIMENT joué (pas
    // preview/démo), on prévient le prof qui l'a assigné. Best-effort — l'edge
    // fn `send-notification` ne notifie que si un assignment existe pour ce
    // couple (élève, exercice), donc un exercice en accès libre ne notifie personne.
    onSuccess: () => {
      const evt = completionNotification(exercise.id, { noWrite });
      if (evt) void sendNotification(evt);
      // XP + HORA crédités : toutes les pages (carte, Mes SKILLCARDS, tableau de
      // bord, Mon année) se mettent à jour sans recharger.
      if (!noWrite) publishProgress('exercise-finished');
    },
  });

  // Pas jouables : chaque question à corrigé pas-à-pas se déploie en un pas par
  // étape (sous-QCM). Rétro-compatible (1 correction → 1 pas).
  const playSteps = useMemo(() => buildPlaySteps(stepBlocks, blocks), [stepBlocks, blocks]);
  const totalSteps = playSteps.length;
  /** Nombre de sous-QCM (checkpoints) = dénominateur du bilan. */
  const totalCheckpoints = useMemo(() => playSteps.filter((s) => s.checkpoint).length, [playSteps]);
  const currentStep: PlayStep | null = playSteps[currentIdx] ?? null;
  const currentBlock = currentStep?.block ?? null;
  const currentCheckpoint = currentStep?.checkpoint ?? null;
  // « Cours » : SKILLCARDS reliées à la question courante, en mode leçon.
  const [showLesson, setShowLesson] = useState(false);
  const lessonQuestionId =
    currentBlock?.block_type === 'statement_question' ? currentBlock.id : null;
  const lessonCards = useMemo(
    () => cardsForQuestion(cardLinks.data ?? [], lessonQuestionId),
    [cardLinks.data, lessonQuestionId],
  );
  const currentAnswer = currentCheckpoint ? answers[currentCheckpoint.id] : undefined;
  const canAdvance = !currentCheckpoint || !!currentAnswer;

  // ─── Feuille de brouillon (docs/PLAN_FEUILLE_DE_BROUILLON.md, lot 4) ─────────
  // Une feuille par question qui en a une (publiée ; brouillons en aperçu
  // tuteur), gardée pendant toute la tentative. Aucune IA : le moteur
  // @jeraisonne/reasoner juge chaque ligne sur l'appareil.
  // Élève : on ne cherche les feuilles que si l'exercice est relié à une
  // SKILLCARD publiée (seuls ceux-là en ont) — pas d'appel de plus sinon.
  // Feuille LIBRE (1re) : une question sans feuille publiée en reçoit une pour
  // calculer, sans HORA (le moteur vérifie les calculs numériques). Niveau =
  // celui du chapitre, sinon celui de l'exercice (exercices de SKILLCARD).
  const { data: exerciseLevel = null } = useQuery({
    queryKey: ['exercise-level', exercise.id],
    enabled: !demoMode && !!exercise.id,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<string | null> => {
      const { data } = await (supabase as unknown as SupabaseClient)
        .from('exercises')
        .select('level, chapter:chapters(level)')
        .eq('id', exercise.id)
        .maybeSingle();
      const row = data as {
        level?: string | null;
        chapter?: { level?: string | null } | null;
      } | null;
      return row?.chapter?.level ?? row?.level ?? null;
    },
  });
  const freeSheets = useMemo(
    () =>
      paper || (!demoMode && isFreeSheetLevel(exerciseLevel))
        ? {
            numbersFor: (questionId: string) => {
              const step = playSteps.find((ps) => ps.block.id === questionId);
              return numbersFromBlocks(step ? [...step.leadIn, step.block] : []);
            },
            // feuille libre du moteur v2 : construite à partir de l'énoncé et des propositions
            modelFor: (questionId: string) => {
              const step = playSteps.find((ps) => ps.block.id === questionId);
              if (!step) return null;
              const cp = orderedCheckpointsFor(blocks, questionId)[0];
              const choices = cp
                ? [cp, ...childrenOf(blocks, cp.id).filter((b) => b.block_type === 'distractor')]
                : [];
              return freeModelInputOf([...step.leadIn, step.block], choices, exerciseLevel);
            },
          }
        : null,
    [paper, demoMode, exerciseLevel, playSteps, blocks],
  );
  // calculatrice non autorisée : son résultat coûte des HORA selon la difficulté
  // de la question (sinon celle de l'exercice)
  const calculatorCostFor = useCallback(
    (questionId: string) => {
      const step = playSteps.find((ps) => ps.block.id === questionId);
      const d = (step?.block.data as { difficulty?: string } | undefined)?.difficulty;
      return calculatorCost(requiresCalculator, d ?? exercise.difficulty);
    },
    [playSteps, requiresCalculator, exercise.difficulty],
  );
  const scratch = useScratchSheets({
    exerciseId: exercise.id,
    attemptId: noWrite ? null : attemptId,
    mode: demoMode ? 'demo' : previewMode ? 'preview' : 'student',
    record: !noWrite,
    // démo : la RPC anonyme filtre elle-même (questions reliées à une SKILLCARD)
    enabled: paper || previewMode || demoMode || (cardLinks.data?.length ?? 0) > 0,
    calculator: requiresCalculator,
    calculatorCostFor,
    free: freeSheets,
  });
  const scratchQuestionId =
    currentBlock?.block_type === 'statement_question' ? currentBlock.id : null;
  const scratchSession = scratch.get(scratchQuestionId);
  // feuille du moteur v2 (lot V4) : l'interface du prototype, reliée à `scratchSession`
  // (qui enregistre les lignes, compte les HORA et coche « Dans ton brouillon »)
  const v2Session = paper ? scratch.getV2(scratchQuestionId) : null;
  const v2Ui = scratch.v2Ui;
  const scratchFound = useFoundChoices(scratchSession);
  const scratchCrossed = useCrossedChoices(scratchSession);
  const [scratchOpen, setScratchOpen] = useState<string | null>(null);

  // ─── Se filmer pendant l'exercice (défi streamé, 31/10/2026) ──────────────
  // L'élève l'accepte au début (icône caméra), peut couper à tout moment, sinon
  // l'enregistrement s'arrête 5 s après la note. Sous la note : revoir,
  // enregistrer, publier comme défi streamé. Jamais en aperçu ni en démo.
  const streamAllowed = !noWrite && !!userId;
  const answeredList = Object.values(answers);
  const exerciseStream = useExerciseStream({
    questionId: scratchQuestionId,
    grade: showResult
      ? {
          correct: answeredList.filter((a) => a.was_correct).length,
          total: answeredList.length,
        }
      : null,
  });
  // chaque nouvelle réponse entre dans la chronologie (question, choix, juste ou non)
  const streamMarked = useRef(new Set<string>());
  const markStream = exerciseStream.mark;
  useEffect(() => {
    for (const [cpId, a] of Object.entries(answers)) {
      if (streamMarked.current.has(cpId)) continue;
      streamMarked.current.add(cpId);
      const questionId = blocks.find((b) => b.id === cpId)?.parent_id ?? cpId;
      markStream({
        kind: 'answer',
        block_id: questionId,
        choice_ids: a.chosen_ids ?? [],
        correct: a.was_correct,
      });
    }
  }, [answers, blocks, markStream]);
  const [streamConsent, setStreamConsent] = useState(false);
  const streamRecording =
    exerciseStream.status === 'recording' || exerciseStream.status === 'starting';
  // une seule vidéo par tentative : une fois finie, annulée ou publiée, plus de bouton caméra
  const onStreamButton =
    streamAllowed &&
    exerciseStream.supported &&
    (exerciseStream.status === 'off' || exerciseStream.status === 'error' || streamRecording)
      ? () => (streamRecording ? exerciseStream.stop() : setStreamConsent(true))
      : undefined;
  const streamOverlays = (
    <>
      <StreamConsentDialog
        open={streamConsent}
        onCancel={() => setStreamConsent(false)}
        onAccept={() => {
          setStreamConsent(false);
          exerciseStream.start();
        }}
      />
      {exerciseStream.status === 'recording' && exerciseStream.preview && (
        <StreamPreview
          stream={exerciseStream.preview}
          elapsed={exerciseStream.elapsed}
          onStop={exerciseStream.stop}
        />
      )}
    </>
  );

  // ─── Boîte à outils (doc « Outils pédagogiques des SKILLCARDS ») ──────────
  // Outils choisis selon le niveau de l'exercice et les SKILLCARDS de la
  // question : d'abord les outils que leurs auteurs y ont reliés (`card_tools`,
  // N-N), sinon ceux que les cartes désignent ou de leur domaine. Ouverte
  // depuis la feuille (« sheet »), un outil peut poser une valeur dans la
  // ligne en cours, au prix d'un résultat de calculatrice. La fenêtre Cours
  // ouvre directement un outil relié (`toolboxTool`).
  const [toolbox, setToolbox] = useState<null | 'player' | 'sheet'>(null);
  const [toolboxTool, setToolboxTool] = useState<ToolId | null>(null);
  const lessonCardIds = useMemo(() => lessonCards.map((c) => c.card_id), [lessonCards]);
  const lessonPublicCards = usePublicCards(lessonCardIds);
  const toolboxContext = useMemo<ToolboxContext>(() => {
    const byId = new Map(lessonPublicCards.map((p) => [p.id, p]));
    const cards = lessonCards.map((c) => {
      const pub = byId.get(c.card_id);
      return pub
        ? {
            domain_label: pub.card.domain_label,
            topic: pub.card.topic,
            title: pub.card.title,
            tools: pub.tools,
          }
        : { title: c.title };
    });
    return contextForCards(cards, exerciseLevel ?? lessonCards[0]?.levels[0] ?? null);
  }, [exerciseLevel, lessonCards, lessonPublicCards]);
  const hasTools = useMemo(() => {
    const sel = toolsForContext(toolboxContext);
    return sel.featured.length + sel.others.length > 0;
  }, [toolboxContext]);
  /** Toutes les étapes de la question sont répondues : la feuille se relit seulement. */
  const questionAnswered = useCallback(
    (questionId: string) => {
      const cps = orderedCheckpointsFor(blocks, questionId);
      return cps.length > 0 && cps.every((cp) => !!answers[cp.id]);
    },
    [blocks, answers],
  );
  const lockAnswered = scratch.lockAnswered;
  useEffect(() => {
    lockAnswered(questionAnswered);
  }, [lockAnswered, questionAnswered, scratchSession]);
  // la question change : la feuille se ferme (elle reste prête pour un retour)
  useEffect(() => {
    setScratchOpen(null);
    setToolbox(null);
    setToolboxTool(null);
  }, [currentIdx]);
  // Une feuille qui rapporte des HORA s'ouvre d'elle-même à l'arrivée sur la question (une fois : l'élève qui
  // revient aux touches n'est pas renvoyé au brouillon). La feuille pour calculer, sans HORA, reste fermée.
  const autoOpenedSheets = useRef(new Set<string>());
  const scratchHasHora = sheetOpensByDefault(scratchSession, !!v2Session?.free);
  useEffect(() => {
    if (!paper || !scratchQuestionId || !scratchHasHora) return;
    if (autoOpenedSheets.current.has(scratchQuestionId)) return;
    autoOpenedSheets.current.add(scratchQuestionId);
    setScratchOpen(scratchQuestionId);
  }, [paper, scratchQuestionId, scratchHasHora]);
  // Exercice fondamental : numéro de chaque question (pour les séparateurs).
  const independentQuestions = !!exercise.is_fundamental;
  const questionNumbers = useMemo(() => {
    const map = new Map<string, number>();
    for (const ps of playSteps) {
      if (ps.block.block_type === 'statement_question' && !map.has(ps.block.id)) {
        map.set(ps.block.id, map.size + 1);
      }
    }
    return map;
  }, [playSteps]);
  /** Séparateur à placer AVANT ce pas (1re étape d'une question, hors la toute première). */
  const separatorBefore = (ps: PlayStep | null, idx: number) => {
    if (!independentQuestions || !ps || !ps.showPrompt || idx === 0) return null;
    const n = questionNumbers.get(ps.block.id);
    return n && n > 1 ? <QuestionSeparator index={n} total={questionNumbers.size} /> : null;
  };

  // ─── Chasse aux indices (clue_hunt) ─────────────────────────────────────────
  // Un bloc `clue_hunt` (racine OU enfant de la question courante) rend une
  // carte inline avec ses cases indices + une barre sticky bottom minimaliste
  // (intro + chrono). La popup révélation vient EN BAS (fixed). Le scoring est
  // CLIENT — la XP s'ajoute à `sessionXp` en fin de chasse.
  const clueScopeRef = useRef<HTMLDivElement | null>(null);
  const [clueHuntsDone, setClueHuntsDone] = useState<Set<string>>(() => new Set());
  /** State final des chasses terminées : conservé après démontage du provider
      pour rendre les cartes de questions passées en mode "read-only historique"
      (les indices révélés restent visibles dans PastStepCard). */
  const [frozenClueHunts, setFrozenClueHunts] = useState<
    Map<string, { revealed: string[]; earned: Array<[string, number]> }>
  >(() => new Map());
  /** Union des clues de toutes les chasses FROZEN — passé à
      ClueTargetsRenderer pour surligner leurs indices dans le scope. */
  const frozenExtraClues = useMemo(() => {
    const out: Array<{ id: string; source_text: string }> = [];
    for (const [clueHuntId] of frozenClueHunts) {
      const block = blocks.find((b) => b.id === clueHuntId);
      if (!block) continue;
      const clues = (block.data as { clues?: Array<{ id: string; source_text: string }> }).clues;
      if (Array.isArray(clues)) out.push(...clues);
    }
    return out;
  }, [blocks, frozenClueHunts]);
  const frozenRevealedSet = useMemo(() => {
    const s = new Set<string>();
    for (const [, state] of frozenClueHunts) {
      for (const id of state.revealed) s.add(id);
    }
    return s;
  }, [frozenClueHunts]);
  /** Map question_id → bloc clue_hunt enfant (rendu dans la carte de la question). */
  const clueHuntByParent = useMemo(() => {
    const map = new Map<string, ExerciseBlock>();
    for (const b of blocks) {
      if (b.block_type !== 'clue_hunt') continue;
      if (b.parent_id === null) continue;
      map.set(b.parent_id, b);
    }
    return map;
  }, [blocks]);

  const activeClueHunt = useMemo(() => {
    // NB : on ne filtre PAS les chasses déjà terminées (clueHuntsDone). Le
    // provider reste monté même après done → les indices révélés restent
    // affichés dans la carte inline. La barre fixed top se cache d'elle-même
    // via `ctx.done`. `clueHuntsDone` sert uniquement à éviter de re-créditer
    // la XP (garde-fou côté onCompleted).
    return (
      blocks.find((b) => {
        if (b.block_type !== 'clue_hunt') return false;
        // Racine → toujours actif.
        if (b.parent_id === null) return true;
        // Enfant d'une question → actif seulement quand cette question est courante.
        return currentBlock && b.parent_id === currentBlock.id;
      }) ?? null
    );
  }, [blocks, currentBlock]);

  // Y a-t-il un texte à LIRE pour l'étape courante avant d'exposer les choix ?
  // Vrai si l'énoncé de la question est montré (1re étape) ET non vide, OU si
  // l'étape porte une consigne courte (`prompt_md`) — y compris aux étapes ≥ 2
  // où la question n'est plus réaffichée. Si rien à lire → pas de délai.
  const currentQuestionHasText = useMemo(() => {
    if (stepPromptOf(currentCheckpoint).length > 0) return true;
    if (!currentStep?.showPrompt || currentBlock?.block_type !== 'statement_question') return false;
    const d = currentBlock.data as { prompt_md?: string; sub_statement_md?: string };
    return `${d.prompt_md ?? ''}${d.sub_statement_md ?? ''}`.trim().length > 0;
  }, [currentStep?.showPrompt, currentBlock?.block_type, currentBlock?.data, currentCheckpoint]);

  const progress = totalSteps ? ((currentIdx + (canAdvance ? 1 : 0)) / totalSteps) * 100 : 0;

  // ─── Avance vers l'étape suivante (ou fin / paywall / démo) ─────────────────
  // Logique unique partagée par le tap sur la popup de feedback (étapes QCM) et
  // le bouton flottant (étapes de lecture sans QCM). Sur une étape QCM, le tap
  // qui ferme la popup fait DIRECTEMENT office de « Suivant » — plus besoin d'un
  // second tap.
  const goNext = useCallback(() => {
    if (currentIdx < totalSteps - 1) {
      setCurrentIdx((i) => i + 1);
    } else if (demoMode && onDemoComplete) {
      const answered = Object.values(notes);
      if (paper && answered.length > 0) {
        setDemoStamp({
          correct: answered.filter((n) => n.wasCorrect).length,
          total: answered.length,
        });
      } else {
        onDemoComplete();
      }
    } else if (locked && !skillcardSampleEnd) {
      // Fin de la zone gratuite sans accès → paywall (attempt non terminé).
      setShowPaywall(true);
    } else {
      setShowResult(true);
    }
  }, [currentIdx, totalSteps, demoMode, onDemoComplete, locked, skillcardSampleEnd, paper, notes]);

  // Le tampon reste DEMO_STAMP_DELAY_MS à l'écran, puis la démo passe à l'inscription.
  useEffect(() => {
    if (!demoStamp || !onDemoComplete) return;
    const id = window.setTimeout(onDemoComplete, DEMO_STAMP_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [demoStamp, onDemoComplete]);

  // Reprise d'une tentative déjà entièrement répondue (page rechargée avant de
  // valider l'XP) : on rouvre le bilan — ou le paywall en fin de zone gratuite.
  const resumedAtEndRef = useRef(false);
  useEffect(() => {
    if (!resumeAtEnd || noWrite || resumedAtEndRef.current) return;
    resumedAtEndRef.current = true;
    if (locked && !skillcardSampleEnd) setShowPaywall(true);
    else setShowResult(true);
  }, [resumeAtEnd, noWrite, locked, skillcardSampleEnd]);

  // ─── Déblocage après achat natif (IAP) ─────────────────────────────────────
  // Le paywall a sondé l'accès et confirmé son activation : on réinvalide la
  // requête du player (la RPC renverra l'exercice complet + `access.allowed`)
  // et on retire le paywall. `currentIdx` est CONSERVÉ → l'élève reprend là où
  // il en était (dernière question gratuite, déjà répondue) et « Question
  // suivante » l'amène à la suite fraîchement débloquée. Sur web, ce chemin
  // n'est pas utilisé (le retour Stripe recharge la page via `?pass_success=1`).
  const handleUnlocked = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['exercise-v2-player', exercise.id] });
    // les feuilles de brouillon des questions débloquées
    qc.invalidateQueries({ queryKey: ['scratch-models', exercise.id] });
    qc.invalidateQueries({ queryKey: ['student-subscription'] });
    qc.invalidateQueries({ queryKey: ['student-passes'] });
    setShowPaywall(false);
  }, [qc, exercise.id]);

  // ─── Navigation rapide (bloc fixe haut-droite, parité V1) ───────────────────
  const situationVisuals = situationBlock ? visualsOf(situationBlock) : [];
  const navItems: QuickNavItem[] = [
    ...(situationBlock
      ? [
          {
            id: V2_ENONCE_ANCHOR,
            label: 'Énoncé',
            icon: BookOpen,
            scrollBlock: 'center' as const,
          },
        ]
      : []),
    ...situationVisuals.map((v, i) => ({
      id: `v2-visual-${v.id}`,
      label: v.title ?? `Visuel ${i + 1}`,
      icon: getBlockIcon(v.type),
      scrollBlock: 'center' as const,
    })),
    {
      id: V2_QUESTION_ANCHOR,
      label: 'Question',
      icon: HelpCircle,
      scrollBlock: 'center' as const,
    },
  ];

  // ─── Données pour le partage « Défi » (mappées depuis le modèle V2) ─────────
  const shareCurrentStep =
    currentBlock?.block_type === 'statement_question'
      ? {
          id: currentBlock.id,
          prompt_md: String((currentBlock.data as { prompt_md?: string }).prompt_md ?? ''),
        }
      : null;
  const shareStepHistory = playSteps.slice(0, currentIdx).flatMap((ps) => {
    const cp = ps.checkpoint;
    const ans = cp ? answers[cp.id] : undefined;
    if (!cp || !ans) return [];
    return [
      {
        step: {
          id: ps.block.id,
          prompt_md: String((ps.block.data as { prompt_md?: string }).prompt_md ?? ''),
        },
        result: { wasCorrect: ans.was_correct },
        selectedChoiceId: ans.chosen_id,
      },
    ];
  });
  const showShareButton = !previewMode && !demoMode;

  // Retour du navigateur : ferme la fenêtre ouverte (brouillon, cours,
  // calculatrice, signalement), sinon demande confirmation avant de quitter un
  // exercice en cours (le bilan et le paywall laissent partir librement).
  const exitGuard = useExerciseExitGuard({
    overlayOpen:
      scratchOpen !== null ||
      showLesson ||
      showCalculator ||
      reportFor !== null ||
      toolbox !== null,
    closeOverlay: () => {
      // la Boîte à outils se ferme seule : la feuille qu'elle recouvre reste ouverte
      if (toolbox !== null) {
        setToolbox(null);
        setToolboxTool(null);
        return;
      }
      setScratchOpen(null);
      setShowLesson(false);
      setShowCalculator(false);
      setReportFor(null);
    },
    inProgress: !noWrite && !showResult && !showPaywall && totalSteps > 0,
  });

  const overlays = (
    <>
      {exitGuard.confirming && (
        <ExitConfirmSheet onStay={exitGuard.stay} onLeave={exitGuard.leave} />
      )}
      {unlockPopup && (
        <CelebrationPopup
          emoji="🔓"
          headline={
            access?.product === 'skillcard' || access?.reason === 'skillcard'
              ? 'SKILLCARD DÉBLOQUÉE !'
              : 'C’est débloqué !'
          }
          message="Tu reprends à la question suivante."
          variant="positive"
          autoDismissMs={2600}
          onClose={() => setUnlockPopup(false)}
        />
      )}
      {reward && !paper && (
        <FeedbackPopup
          reward={reward.value}
          wasCorrect={reward.wasCorrect}
          sessionXp={sessionXp}
          explanationMd={reward.explanationMd}
          errorFeedbackMd={reward.errorFeedbackMd}
          extra={
            reward.wasCorrect ? (
              <CardXpGain
                cards={cardsForQuestion(cardLinks.data ?? [], reward.questionId)}
                userId={userId}
              />
            ) : undefined
          }
          onClose={() => {
            // Le tap qui ferme la popup fait office de « Suivant ».
            setReward(null);
            goNext();
          }}
        />
      )}
      {!paper && scratchSession && scratchOpen !== null && scratchOpen === scratchQuestionId && (
        <ScratchSheet
          session={scratchSession}
          draft={scratch.isDraft(scratchQuestionId)}
          onClose={() => setScratchOpen(null)}
          onToolbox={hasTools ? () => setToolbox('sheet') : undefined}
          statement={
            <div className="space-y-2">
              {currentStep?.leadIn.map((lead) => (
                <StatementBlockBody key={lead.id} block={lead} />
              ))}
              {currentBlock && <StatementBlockBody block={currentBlock} />}
              {currentCheckpoint && stepPromptOf(currentCheckpoint) && (
                <div className="text-ink-900 font-sans text-base font-medium leading-relaxed">
                  <MarkdownMath>{stepPromptOf(currentCheckpoint)}</MarkdownMath>
                </div>
              )}
            </div>
          }
        />
      )}
      {toolbox && (
        <ToolboxSheet
          context={toolboxContext}
          initialTool={toolboxTool}
          onClose={() => {
            setToolbox(null);
            setToolboxTool(null);
          }}
          sendCost={scratchSession?.calculatorCost ?? 0}
          onSend={
            toolbox === 'sheet' && scratchSession?.acceptsToolValues
              ? (chip) => {
                  scratchSession.addToolValue(chip);
                  setToolbox(null);
                  setToolboxTool(null);
                }
              : undefined
          }
        />
      )}
      {showCalculator && <CalculatorModal onClose={() => setShowCalculator(false)} />}
      {showLesson && lessonCards.length > 0 && (
        <CardLessonPopup
          cards={lessonCards}
          onClose={() => setShowLesson(false)}
          onOpenTool={(toolId) => {
            setShowLesson(false);
            setToolboxTool(toolId);
            setToolbox('player');
          }}
        />
      )}
      {reportFor && (
        <ReportQuestionSheet
          exerciseId={exercise.id}
          questionBlockId={reportFor}
          questionPrompt={String(
            (blocks.find((b) => b.id === reportFor)?.data as { prompt_md?: string } | undefined)
              ?.prompt_md ?? '',
          )}
          attemptId={noWrite ? null : attemptId}
          onClose={() => setReportFor(null)}
        />
      )}
      {showShareButton && showShareModal && (
        <ChallengeShareModal
          exerciseId={exercise.id}
          exerciseTitle={exercise.title}
          subject={subject}
          level={level}
          chapter={chapter}
          phase="exercise"
          attemptId={attemptId}
          currentStepIdx={currentIdx}
          currentStep={shareCurrentStep}
          stepHistory={shareStepHistory}
          userId={userId}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </>
  );

  // ─── Paywall ────────────────────────────────────────────────────────────
  // L'élève a fini la zone gratuite sans accès : on bloque la suite ici.
  // On NE termine PAS l'attempt (reste `in_progress`) — il pourra reprendre
  // après avoir débloqué.
  if (showPaywall) {
    return (
      <>
        <ExerciseHeader
          exerciseId={exercise.id}
          chapterId={exercise.chapter_id}
          illustrationUrl={exercise.illustration_url}
          title={exercise.title}
        />
        <div className="pb-safe mx-auto max-w-2xl space-y-4 p-4 lg:max-w-4xl">
          {isSkillcardPaywall(access) ? (
            <SkillcardPaywallCard
              cardId={access?.skillcard_id ?? ''}
              freeSample={!!access?.free_sample}
              freeQuestions={access?.free_questions ?? 1}
              exerciseId={exercise.id}
              attemptId={attemptId}
              onUnlocked={handleUnlocked}
            />
          ) : (
            <ExercisePaywallCard
              chapterType={chapterType}
              exerciseId={exercise.id}
              attemptId={attemptId}
              onUnlocked={handleUnlocked}
            />
          )}
          <button
            type="button"
            onClick={() => goBack(exercise.id)}
            className="text-ink-500 hover:text-ink-700 mx-auto block min-h-11 font-sans text-sm font-medium"
          >
            Plus tard
          </button>
        </div>
      </>
    );
  }

  // ─── Mode « sur la feuille » : ce qui est déjà écrit (questions passées, leur
  // brouillon, la réponse et la correction du prof). Sert aussi au bilan.
  const paperHistory = (count: number) =>
    playSteps.slice(0, count).map((ps, i) => (
      <Fragment key={`${ps.block.id}-${ps.checkpoint?.id ?? 'x'}-${i}`}>
        {separatorBefore(ps, i)}
        <div className="pp-past">
          {ps.leadIn.map((lead) => (
            <PastStepCard
              key={`lead-${lead.id}`}
              block={lead}
              checkpoint={null}
              answer={undefined}
              blocks={blocks}
              paper
            />
          ))}
          <PastStepCard
            block={ps.block}
            checkpoint={ps.checkpoint}
            answer={ps.checkpoint ? answers[ps.checkpoint.id] : undefined}
            showPrompt={ps.showPrompt}
            stepInfo={ps.stepCount > 1 ? { index: ps.stepIndex, count: ps.stepCount } : null}
            blocks={blocks}
            frozenClueHunts={frozenClueHunts}
            onReport={canReport && ps.checkpoint ? () => setReportFor(ps.block.id) : undefined}
            scratch={
              ps.block.block_type === 'statement_question' &&
              playSteps[i + 1]?.block.id !== ps.block.id ? (
                <ScratchLinesPreview session={scratch.get(ps.block.id)} />
              ) : null
            }
            paper
          />
          {ps.checkpoint && notes[ps.checkpoint.id] && (
            <SheetTeacherNote note={notes[ps.checkpoint.id]!} past cards={[]} userId={userId} />
          )}
        </div>
      </Fragment>
    ));

  // ─── Bilan ────────────────────────────────────────────────────────────────
  if (showResult) {
    const correctCount = Object.values(answers).filter((a) => a.was_correct).length;
    // Questions répondues JUSTE (parent du checkpoint) → XP des SKILLCARDS.
    const correctQuestionIds = Object.values(answers)
      .filter((a) => a.was_correct)
      .map((a) => blocks.find((b) => b.id === a.correctionId)?.parent_id)
      .filter((id): id is string => !!id);
    // HORA du brouillon des questions RÉPONDUES (justes ou non) → carte de la question
    // (migration 0369 : crédités par le serveur à la fin de l'exercice).
    const answeredQuestionIds = new Set(
      Object.values(answers)
        .map((a) => blocks.find((b) => b.id === a.correctionId)?.parent_id)
        .filter((id): id is string => !!id),
    );
    const scratchByQuestion = Object.fromEntries(
      Object.entries(scratch.horaByQuestion()).filter(([qid]) => answeredQuestionIds.has(qid)),
    );
    const cardGains = noWrite
      ? []
      : cardXpGains(
          cardLinks.data ?? [],
          correctQuestionIds,
          gainsBaseline ?? [],
          scratchByQuestion,
        );
    // Avant de partir payer : reprise à la question suivante au retour
    // (tentative gardée si non validée, sinon nouvelle tentative) + sortie.
    const rememberBeforeUnlock = () => {
      rememberResume(exercise.id, {
        attemptId: finishMutation.isSuccess ? null : attemptId,
        stepIdx: totalSteps,
      });
      rememberExerciseExit(exercise.id, `/card/${access?.skillcard_id ?? ''}`);
    };
    const resultScreen = (
      <ResultScreen
        paper={paper}
        exercise={exercise}
        blocks={blocks}
        correctCount={correctCount}
        totalCheckpoints={totalCheckpoints}
        elapsedMs={elapsedMs}
        previewMode={previewMode}
        serverScore={finishMutation.data?.score ?? null}
        xpAwarded={finishMutation.data?.xp_awarded ?? null}
        sessionXp={sessionXp}
        isValidating={finishMutation.isPending}
        isValidated={finishMutation.isSuccess}
        validateError={finishMutation.isError}
        onValidate={() => finishMutation.mutate()}
        cardGains={cardGains}
        coverOverride={
          skillcardSampleEnd && canBuySkillcardsInApp() ? (
            <SkillcardUnlockCover
              cardId={access?.skillcard_id ?? ''}
              userId={userId || null}
              remaining={remainingLockedQuestions(access)}
              onUnlock={() => {
                rememberBeforeUnlock();
                window.location.href = skillcardBuyPath(
                  access?.skillcard_id ?? '',
                  window.location.pathname,
                );
              }}
            />
          ) : undefined
        }
        continueHidden={streamPending(exerciseStream.status)}
        footer={withTrousseWheel(
          finishMutation.isSuccess && !noWrite && !!userId,
          streamRecording || exerciseStream.result || skillcardSampleEnd ? (
            <div className="space-y-3">
              {streamRecording && (
                <StreamRecordingActions
                  elapsed={exerciseStream.elapsed}
                  onStop={exerciseStream.stop}
                  onDiscard={exerciseStream.discard}
                />
              )}
              {exerciseStream.result && attemptId && userId && (
                <StreamResultActions
                  result={exerciseStream.result}
                  exerciseId={exercise.id}
                  exerciseTitle={exercise.title}
                  attemptId={attemptId}
                  userId={userId}
                  published={exerciseStream.status === 'published'}
                  onPublished={exerciseStream.markPublished}
                  onDiscard={exerciseStream.discard}
                />
              )}
              {skillcardSampleEnd && (
                <div className="space-y-3">
                  {remainingLockedQuestions(access) > 0 && (
                    <p className="font-display text-accent-500 text-center text-lg font-extrabold uppercase tracking-wide">
                      Fais l’exercice complet, il reste {remainingLockedQuestions(access)} question
                      {remainingLockedQuestions(access) > 1 ? 's' : ''} !
                    </p>
                  )}
                  {!canBuySkillcardsInApp() ? (
                    // iOS : pas d'achat de carte (App Store 3.1.1) → paywall historique.
                    <SkillcardPaywallCard
                      cardId={access?.skillcard_id ?? ''}
                      freeSample={!!access?.free_sample}
                      freeQuestions={access?.free_questions ?? 1}
                      exerciseId={exercise.id}
                      attemptId={null}
                      onUnlocked={handleUnlocked}
                    />
                  ) : (
                    // Mêmes offres que la page d'achat ; retour sur l'exercice, qui
                    // repart de zéro (tentative terminée).
                    <>
                      <SkillcardOffersPanel
                        cardId={access?.skillcard_id ?? ''}
                        userId={userId || null}
                        signedIn={!!userId}
                        returnPath={window.location.pathname}
                        onBeforeCheckout={rememberBeforeUnlock}
                      />
                      {/* App native : l'accès complet (pass du store) reste proposé. */}
                      {isNativeApp() && (
                        <SkillcardPaywallCard
                          cardId={access?.skillcard_id ?? ''}
                          freeSample={!!access?.free_sample}
                          freeQuestions={access?.free_questions ?? 1}
                          exerciseId={exercise.id}
                          attemptId={null}
                          onUnlocked={handleUnlocked}
                        />
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ) : undefined,
        )}
        onExit={() => {
          if (onExitPreview) onExitPreview();
          else goBack(exercise.id);
        }}
      />
    );
    if (paper) {
      // Sur la feuille : toute la copie corrigée, puis la note du prof en bas.
      return (
        <PaperPage>
          {previewMode && <PreviewBanner onExit={onExitPreview} />}
          <ExerciseHeader
            exerciseId={exercise.id}
            chapterId={exercise.chapter_id}
            illustrationUrl={exercise.illustration_url}
            title={exercise.title}
            progress={100}
            elapsedLabel={formatTimer(elapsedMs)}
          />
          <PaperColumn>
            <div className="pt-4">
              <h2 className="font-display text-ink-900 mb-3 text-lg font-bold">
                <MathText>{exercise.title}</MathText>
              </h2>
              {situationBlock && (
                <div className="pb-3">
                  <SituationBody block={situationBlock} />
                </div>
              )}
              {paperHistory(totalSteps)}
              {resultScreen}
            </div>
          </PaperColumn>
          {streamOverlays}
        </PaperPage>
      );
    }
    return (
      <>
        {previewMode && <PreviewBanner onExit={onExitPreview} />}
        {resultScreen}
        {streamOverlays}
      </>
    );
  }

  // ─── Aucun pas à jouer ────────────────────────────────────────────────────
  if (totalSteps === 0 || !currentBlock) {
    return (
      <>
        {previewMode && <PreviewBanner onExit={onExitPreview} />}
        <ExerciseHeader
          exerciseId={exercise.id}
          chapterId={exercise.chapter_id}
          illustrationUrl={exercise.illustration_url}
          title={exercise.title}
        />
        <div className="pb-safe mx-auto max-w-2xl space-y-4 p-4 lg:max-w-4xl">
          <h2 className="font-display text-ink-900 text-base font-bold">
            <MathText>{exercise.title}</MathText>
          </h2>
          {situationBlock && (
            <div className="border-primary-200 bg-primary-50/40 rounded-2xl border p-4">
              <StatementBlockBody block={situationBlock} />
            </div>
          )}
          <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            Cet exercice n'a aucune étape à jouer.
          </p>
        </div>
      </>
    );
  }

  // ─── Rendu principal ──────────────────────────────────────────────────────
  // Fin d'une chasse aux indices (les deux rendus).
  const onClueHuntCompleted = (
    final: { totalXp: number; revealed: string[]; earned: Array<[string, number]> },
    id: string,
  ) => {
    setClueHuntsDone((prev) => new Set(prev).add(id));
    setFrozenClueHunts((prev) => {
      const next = new Map(prev);
      next.set(id, { revealed: final.revealed, earned: final.earned });
      return next;
    });
    if (final.totalXp > 0) setSessionXp((prev) => prev + final.totalXp);
    // Après 1s, on scrolle en bas pour révéler les QcmChoices — l'élève
    // vient de finir la chasse, il peut répondre à la question.
    window.setTimeout(() => {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: 'smooth',
      });
    }, 1000);
  };

  // Réponse « calculatrice » (générateur paramétrique) : la même dans les deux rendus.
  const calculatorAnswer = currentCheckpoint &&
    checkpointAnswerMode(currentCheckpoint) === 'calculator' && (
      <div
        className={cn(
          'transition-opacity',
          activeClueHunt &&
            !clueHuntsDone.has(activeClueHunt.id) &&
            'pointer-events-none select-none opacity-20',
        )}
        aria-hidden={activeClueHunt && !clueHuntsDone.has(activeClueHunt.id) ? true : undefined}
      >
        <VariableExpressionAnswer
          checkpoint={currentCheckpoint}
          answerValue={currentAnswer?.answer_value}
          wasCorrect={currentAnswer?.was_correct}
          pending={submitNumeric.isPending}
          error={answerError}
          onSubmit={async (value) => {
            if (currentAnswer || answeringRef.current) return;
            answeringRef.current = true;
            setAnswerError(null);
            const timeMs = Date.now() - questionShownAtRef.current;
            let res: { was_correct: boolean };
            try {
              res = await submitNumeric.mutateAsync({
                correction_block_id: currentCheckpoint.id,
                value,
                time_ms: timeMs,
              });
            } catch {
              setAnswerError(ANSWER_NOT_SENT);
              return;
            } finally {
              answeringRef.current = false;
            }
            setAnswers((prev) => ({
              ...prev,
              [currentCheckpoint.id]: {
                correctionId: currentCheckpoint.id,
                chosen_id: '',
                chosen_ids: [],
                was_correct: res.was_correct,
                answer_value: value,
              },
            }));
            const xp = computeXpReward(res.was_correct, streak, timeMs);
            setStreak(xp.streak);
            setSessionXp((total) => total + xp.total);
            const explanationMd = String(
              (currentCheckpoint.data as { explanation_md?: string }).explanation_md ?? '',
            );
            showReward(currentCheckpoint.id, {
              value: xp,
              wasCorrect: res.was_correct,
              explanationMd,
              errorFeedbackMd: '',
              questionId: currentCheckpoint.parent_id ?? currentBlock?.id ?? null,
            });
          }}
        />
      </div>
    );

  // Envoi d'une réponse QCM (cartes ou touches du dock « sur la feuille »).
  const submitQcm = async (chosenIds: string[]) => {
    if (!currentCheckpoint) return;
    if (currentAnswer || answeringRef.current || chosenIds.length === 0) return;
    answeringRef.current = true;
    setAnswerError(null);
    const multiple = checkpointAnswerMode(currentCheckpoint) === 'multiple';
    // Temps de réflexion réel : de l'apparition de la question
    // à la soumission (≠ aller-retour réseau) → base du bonus
    // de vitesse, côté affichage ET côté crédit serveur.
    const timeMs = Date.now() - questionShownAtRef.current;
    let res: { was_correct: boolean };
    try {
      res = await submit.mutateAsync({
        correction_block_id: currentCheckpoint.id,
        chosen_block_ids: chosenIds,
        multiple,
        time_ms: timeMs,
      });
    } catch {
      setAnswerError(ANSWER_NOT_SENT);
      return;
    } finally {
      answeringRef.current = false;
    }
    setAnswers((prev) => ({
      ...prev,
      [currentCheckpoint.id]: {
        correctionId: currentCheckpoint.id,
        chosen_id: chosenIds[0] ?? '',
        chosen_ids: chosenIds,
        was_correct: res.was_correct,
      },
    }));
    // Récompense gamifiée : calcule l'XP (base + bonus de série
    // + bonus de vitesse selon le temps de réflexion), met à jour
    // la série + le cumul, et déclenche la popup centrée.
    const xp = computeXpReward(res.was_correct, streak, timeMs);
    setStreak(xp.streak);
    setSessionXp((total) => total + xp.total);
    // Sur erreur : explication du corrigé + feedback du 1er choix
    // coché qui était FAUX (distracteur non-correct).
    const explanationMd = String(
      (currentCheckpoint.data as { explanation_md?: string }).explanation_md ?? '',
    );
    const wrongChosen = chosenIds
      .map((id) => blocks.find((b) => b.id === id))
      .find(
        (b) =>
          !!b &&
          b.id !== currentCheckpoint.id &&
          (b.data as { is_correct?: unknown }).is_correct !== true,
      );
    const errorFeedbackMd = String(
      (wrongChosen?.data as { feedback_md?: string } | undefined)?.feedback_md ?? '',
    );
    showReward(currentCheckpoint.id, {
      value: xp,
      wasCorrect: res.was_correct,
      explanationMd,
      errorFeedbackMd,
      questionId: currentCheckpoint.parent_id ?? currentBlock?.id ?? null,
    });
  };

  // ─── Exercice « sur la feuille de brouillon » (drapeau `paper_player`) ──────
  // Fond intégral de papier, questions écrites dessus, réponses en touches dans
  // le dock du bas (sélection puis « Check »), brouillon en ligne sous la question.
  if (paper) {
    const writing = !!scratchQuestionId && scratchOpen === scratchQuestionId;
    const answerModeNow = currentCheckpoint ? checkpointAnswerMode(currentCheckpoint) : null;
    const cluePending = !!activeClueHunt && !clueHuntsDone.has(activeClueHunt.id);
    const sheetWritable = !!scratchSession && !scratchSession.getSnapshot().locked;
    const mode = dockMode({
      hasCheckpoint: !!currentCheckpoint,
      answerMode: answerModeNow,
      answered: !!currentAnswer,
      clueHuntPending: cluePending,
      canAdvance,
      sheetWritable,
      writing,
    });
    const startWriting = () => {
      if (scratchQuestionId && sheetWritable) setScratchOpen(scratchQuestionId);
    };
    const stopWriting = () => setScratchOpen(null);
    const v2KeyboardPanel =
      v2Session && v2Ui ? (
        <v2Ui.V2Keyboard
          session={v2Session}
          onAnswer={currentCheckpoint ? stopWriting : undefined}
          answerLabel="Répondre"
        />
      ) : null;
    const nextLabel =
      currentIdx >= totalSteps - 1
        ? demoMode
          ? 'Continuer →'
          : locked && !skillcardSampleEnd
            ? 'Débloquer la suite →'
            : 'Terminer l’exercice'
        : 'Suivant';
    return (
      <PaperPage>
        {previewMode && <PreviewBanner onExit={onExitPreview} />}
        <ExerciseHeader
          exerciseId={exercise.id}
          chapterId={exercise.chapter_id}
          illustrationUrl={exercise.illustration_url}
          title={exercise.title}
          progress={progress}
          elapsedLabel={formatTimer(elapsedMs)}
        />
        <ExerciseQuickNav
          items={navItems}
          onShare={showShareButton ? () => setShowShareModal(true) : undefined}
          onCalculator={requiresCalculator ? () => setShowCalculator(true) : undefined}
          onLesson={lessonCards.length > 0 ? () => setShowLesson(true) : undefined}
          onToolbox={hasTools ? () => setToolbox(writing ? 'sheet' : 'player') : undefined}
          onStream={onStreamButton}
          streaming={streamRecording}
          variant="bar"
        />
        {overlays}
        {streamOverlays}
        <ClueHuntProviderWrapper
          clueHunt={activeClueHunt}
          scopeRef={clueScopeRef}
          onCompleted={onClueHuntCompleted}
        >
          <div
            ref={clueScopeRef}
            data-clue-scope
            className={cn('relative pt-4', activeClueHunt && 'pt-20')}
          >
            {(activeClueHunt || frozenClueHunts.size > 0) && (
              <ClueTargetsRenderer
                scopeRef={clueScopeRef}
                extraClues={frozenExtraClues}
                extraRevealed={frozenRevealedSet}
              />
            )}
            <PaperColumn>
              <h2 className="font-display text-ink-900 mb-3 text-lg font-bold">
                <MathText>{exercise.title}</MathText>
              </h2>

              {situationBlock && (
                <div id={V2_ENONCE_ANCHOR} className="pb-3" style={{ scrollMarginTop: '4rem' }}>
                  <SituationBody block={situationBlock} />
                </div>
              )}

              {/* Ce qui est déjà écrit : questions passées, leur brouillon, la réponse. */}
              {paperHistory(currentIdx)}

              {separatorBefore(currentStep, currentIdx)}

              {/* La question en cours, écrite sur la feuille. */}
              <div
                ref={currentStepRef}
                id={V2_QUESTION_ANCHOR}
                className="space-y-3 py-2"
                style={{
                  scrollMarginTop: '4rem',
                  scrollMarginBottom: 'calc(var(--dock-h, 0px) + 1rem)',
                }}
              >
                {currentStep?.leadIn.map((lead) => (
                  <StatementBlockBody key={lead.id} block={lead} />
                ))}
                {currentStep?.showPrompt && <StatementBlockBody block={currentBlock} />}
                {currentBlock &&
                  currentBlock.block_type === 'statement_question' &&
                  clueHuntByParent.get(currentBlock.id) && (
                    <ClueHuntCasesInline
                      data={clueHuntByParent.get(currentBlock.id)!.data as unknown as ClueHuntData}
                      autoCollapse={!!currentAnswer}
                    />
                  )}
                {currentStep && currentStep.stepCount > 1 && (
                  <p className="font-display text-primary-600 text-[11px] font-semibold uppercase tracking-widest">
                    Étape {currentStep.stepIndex + 1} / {currentStep.stepCount}
                  </p>
                )}
                {currentCheckpoint && stepPromptOf(currentCheckpoint) && (
                  <div className="text-ink-900 font-sans text-base font-medium leading-relaxed">
                    <MarkdownMath>{stepPromptOf(currentCheckpoint)}</MarkdownMath>
                  </div>
                )}

                {currentCheckpoint && answerModeNow !== 'calculator' && (
                  <PaperQuestionAnswer
                    key={currentCheckpoint.id}
                    checkpoint={currentCheckpoint}
                    blocks={blocks}
                    seed={seed + currentIdx}
                    answer={currentAnswer}
                    hasText={currentQuestionHasText}
                    pending={submit.isPending}
                    error={answerError}
                    found={scratchFound}
                    crossed={scratchCrossed}
                    onSubmit={submitQcm}
                    mode={mode}
                    session={scratchSession}
                    canWrite={sheetWritable}
                    onWrite={startWriting}
                    onDoneWriting={stopWriting}
                    writePanel={v2KeyboardPanel}
                    onNext={goNext}
                    nextLabel={nextLabel}
                    hideDock={!!demoStamp}
                  />
                )}

                {v2Session && v2Ui && scratchQuestionId ? (
                  <div className="mt-2">
                    <v2Ui.V2SheetView
                      session={v2Session}
                      onActivate={startWriting}
                      // démo sans compte : pas d'avis (écrit sous le compte connecté, jamais en anonyme)
                      feedback={
                        demoMode
                          ? undefined
                          : {
                              context: 'exercise',
                              exerciseId: exercise.id,
                              questionId: scratchQuestionId,
                              attemptId: noWrite ? null : attemptId,
                            }
                      }
                      reviewKey={`${exercise.id}:${scratchQuestionId}`}
                      // l'avis se pose sous la note du prof (plus bas), pas sous la feuille
                      showReview={false}
                      canSeeSolution={
                        authProfile?.role === 'tutor' || authProfile?.role === 'admin'
                      }
                    />
                  </div>
                ) : (
                  scratchSession &&
                  scratchQuestionId && (
                    <InlineSheet
                      session={scratchSession}
                      draft={scratch.isDraft(scratchQuestionId)}
                      writing={writing}
                      onActivate={startWriting}
                    />
                  )
                )}

                {calculatorAnswer}

                {currentCheckpoint && notes[currentCheckpoint.id] && (
                  <SheetTeacherNote
                    key={`note-${currentCheckpoint.id}`}
                    note={notes[currentCheckpoint.id]!}
                    cards={cardsForQuestion(
                      cardLinks.data ?? [],
                      notes[currentCheckpoint.id]!.questionId,
                    )}
                    userId={userId}
                  />
                )}

                {/* Avis sur le brouillon, une fois la note du prof écrite (jamais en démo sans compte). */}
                {!demoMode &&
                  v2Session &&
                  v2Ui &&
                  scratchQuestionId &&
                  currentCheckpoint &&
                  notes[currentCheckpoint.id] && (
                    <v2Ui.V2SheetReview
                      session={v2Session}
                      feedback={{
                        context: 'exercise',
                        exerciseId: exercise.id,
                        questionId: scratchQuestionId,
                        attemptId: noWrite ? null : attemptId,
                      }}
                      reviewKey={`${exercise.id}:${scratchQuestionId}`}
                    />
                  )}

                {demoStamp && (
                  <PaperFinalGrade>
                    <TeacherScoreStamp correct={demoStamp.correct} total={demoStamp.total} />
                  </PaperFinalGrade>
                )}
              </div>
            </PaperColumn>

            {activeClueHunt && <ClueHuntStickyBar />}
            {activeClueHunt && <CluePopupFromCtx />}
            {activeClueHunt && <FloatingXpBadgeFromCtx />}
          </div>
        </ClueHuntProviderWrapper>

        {/* Dock quand il n'y a pas de QCM (étape de lecture, réponse calculatrice). */}
        {!demoStamp &&
          !(currentCheckpoint && answerModeNow !== 'calculator') &&
          (mode === 'next' || (answerModeNow === 'calculator' && !!currentAnswer) ? (
            <PaperNextButton label={nextLabel} onClick={goNext} />
          ) : mode === 'write' && scratchSession ? (
            <AnswerDock label="Feuille de brouillon">
              {v2KeyboardPanel ?? <PaletteInDock session={scratchSession} onDone={stopWriting} />}
            </AnswerDock>
          ) : null)}
      </PaperPage>
    );
  }

  return (
    <>
      {previewMode && <PreviewBanner onExit={onExitPreview} />}
      <ExerciseHeader
        exerciseId={exercise.id}
        chapterId={exercise.chapter_id}
        illustrationUrl={exercise.illustration_url}
        title={exercise.title}
        progress={progress}
        elapsedLabel={formatTimer(elapsedMs)}
      />
      <ExerciseQuickNav
        items={navItems}
        onShare={showShareButton ? () => setShowShareModal(true) : undefined}
        onCalculator={requiresCalculator ? () => setShowCalculator(true) : undefined}
        onLesson={lessonCards.length > 0 ? () => setShowLesson(true) : undefined}
        onToolbox={hasTools ? () => setToolbox('player') : undefined}
        onStream={onStreamButton}
        streaming={streamRecording}
      />
      {overlays}
      {streamOverlays}
      {/* pb-32 : garde l'espace en bas pour que le dernier paragraphe reste
          lisible quand le bouton flottant `NextButton` est affiché.
          Le ClueHuntProvider n'englobe le contenu QUE quand une chasse est
          active — hors chasse, useClueHuntCtx() retourne null (rendu neutre). */}
      <ClueHuntProviderWrapper
        clueHunt={activeClueHunt}
        scopeRef={clueScopeRef}
        onCompleted={onClueHuntCompleted}
      >
        <div
          ref={clueScopeRef}
          data-clue-scope
          className={cn(
            'pb-safe relative mx-auto max-w-2xl p-4 pb-32 lg:max-w-4xl',
            // Padding-top ADDITIONNEL quand la barre chasse aux indices (fixed top)
            // est affichée, pour que le contenu ne passe pas dessous.
            activeClueHunt && 'pt-20',
          )}
        >
          {/* ClueTargetsRenderer rend les encadrés verts autour des indices TROUVÉS
            partout dans le scope. On lui donne : (a) les clues du bloc actif via
            son ctx interne, (b) les clues des chasses TERMINÉES (frozen) pour
            que les indices restent surlignés quand l'élève avance. Rendu dès
            qu'il y a AU MOINS un clue à surligner (actif OU frozen). */}
          {(activeClueHunt || frozenClueHunts.size > 0) && (
            <ClueTargetsRenderer
              scopeRef={clueScopeRef}
              extraClues={frozenExtraClues}
              extraRevealed={frozenRevealedSet}
            />
          )}
          <h2 className="font-display text-ink-900 mb-3 text-base font-bold">
            <MathText>{exercise.title}</MathText>
          </h2>

          {/* Pile : situation + historique + étape courante.
            Pas d'espace entre les cartes ; coins arrondis seulement sur la
            première et la dernière carte de la pile. */}
          <div className="[&>*:first-child]:rounded-t-2xl [&>*:last-child]:rounded-b-2xl [&>*]:rounded-none">
            {situationBlock && (
              <div
                id={V2_ENONCE_ANCHOR}
                className="border-primary-100 bg-primary-50/40 border px-4 py-3"
                style={{ scrollMarginTop: '4rem' }}
              >
                <p className="font-display text-primary-600 mb-1.5 text-[11px] font-semibold uppercase tracking-widest">
                  La situation
                </p>
                <SituationBody block={situationBlock} />
              </div>
            )}

            {/* Historique : tous les pas déjà parcourus */}
            {playSteps.slice(0, currentIdx).map((ps, i) => {
              const ans = ps.checkpoint ? answers[ps.checkpoint.id] : undefined;
              const separator = separatorBefore(ps, i);
              // Sous-énoncés de la question (avant elle) : affichés juste au-dessus
              // de sa carte, APRÈS le séparateur — ils font partie de cette question.
              const card = (
                <Fragment key={`${ps.block.id}-${ps.checkpoint?.id ?? 'x'}-${i}`}>
                  {ps.leadIn.map((lead) => (
                    <PastStepCard
                      key={`lead-${lead.id}`}
                      block={lead}
                      checkpoint={null}
                      answer={undefined}
                      blocks={blocks}
                    />
                  ))}
                  <PastStepCard
                    block={ps.block}
                    checkpoint={ps.checkpoint}
                    answer={ans}
                    showPrompt={ps.showPrompt}
                    stepInfo={
                      ps.stepCount > 1 ? { index: ps.stepIndex, count: ps.stepCount } : null
                    }
                    blocks={blocks}
                    frozenClueHunts={frozenClueHunts}
                    onReport={
                      canReport && ps.checkpoint ? () => setReportFor(ps.block.id) : undefined
                    }
                    scratch={
                      // Brouillon de la question (dernière étape) : ses lignes, AVANT la réponse.
                      ps.block.block_type === 'statement_question' &&
                      playSteps[i + 1]?.block.id !== ps.block.id ? (
                        <ScratchLinesPreview session={scratch.get(ps.block.id)} />
                      ) : null
                    }
                  />
                </Fragment>
              );
              if (!independentQuestions) return card;
              // Questions indépendantes : séparateur + questions passées estompées.
              return (
                <Fragment key={`${ps.block.id}-${ps.checkpoint?.id ?? 'x'}-${i}`}>
                  {separator}
                  <div className="rounded-2xl opacity-60">{card}</div>
                </Fragment>
              );
            })}

            {separatorBefore(currentStep, currentIdx)}

            {/* Étape courante — ref ciblée par l'auto-scroll. La
              `scroll-margin-bottom` réserve la place du NextButton flottant
              (hauteur du bouton + safe-area iOS + petit gap) pour que le bas
              de la carte ne soit jamais masqué par le bouton après scroll. */}
            <div
              ref={currentStepRef}
              id={V2_QUESTION_ANCHOR}
              className="border-ink-100 space-y-3 border bg-white px-4 py-3"
              style={{
                scrollMarginTop: '4rem',
                scrollMarginBottom: 'calc(6rem + env(safe-area-inset-bottom))',
              }}
            >
              {/* Sous-énoncé(s) de la question : dans SA carte, au-dessus de son énoncé. */}
              {currentStep?.leadIn.map((lead) => (
                <StatementBlockBody key={lead.id} block={lead} />
              ))}
              {currentStep?.showPrompt && <StatementBlockBody block={currentBlock} />}

              {/* Chasse aux indices ENFANT de la question courante : rendue ici,
                dans la carte de la question, plutôt que comme un bloc autonome
                à la fin de la liste. Auto-collapse dès que la question courante
                a été répondue (l'élève passe à l'étape suivante). */}
              {currentBlock &&
                currentBlock.block_type === 'statement_question' &&
                clueHuntByParent.get(currentBlock.id) && (
                  <ClueHuntCasesInline
                    data={clueHuntByParent.get(currentBlock.id)!.data as unknown as ClueHuntData}
                    autoCollapse={!!currentAnswer}
                  />
                )}

              {currentStep && currentStep.stepCount > 1 && (
                <p className="font-display text-primary-600 text-[11px] font-semibold uppercase tracking-widest">
                  Étape {currentStep.stepIndex + 1} / {currentStep.stepCount}
                </p>
              )}

              {/* Consigne courte de l'étape — cadre le sous-QCM (« Les coefficients
                multiplicateurs sont : ») pour que les choix aient un contexte. */}
              {currentCheckpoint && stepPromptOf(currentCheckpoint) && (
                <div className="text-ink-900 font-sans text-base font-medium leading-relaxed">
                  <MarkdownMath>{stepPromptOf(currentCheckpoint)}</MarkdownMath>
                </div>
              )}

              {scratchSession && scratchQuestionId && (
                <ScratchEntryCard
                  session={scratchSession}
                  draft={scratch.isDraft(scratchQuestionId)}
                  answered={questionAnswered(scratchQuestionId)}
                  onOpen={() => setScratchOpen(scratchQuestionId)}
                />
              )}

              {calculatorAnswer}

              {currentCheckpoint && checkpointAnswerMode(currentCheckpoint) !== 'calculator' && (
                <div
                  className={cn(
                    'transition-opacity',
                    activeClueHunt &&
                      !clueHuntsDone.has(activeClueHunt.id) &&
                      'pointer-events-none select-none opacity-20',
                  )}
                  aria-hidden={
                    activeClueHunt && !clueHuntsDone.has(activeClueHunt.id) ? true : undefined
                  }
                >
                  <QcmChoices
                    checkpoint={currentCheckpoint}
                    blocks={blocks}
                    seed={seed + currentIdx}
                    answer={currentAnswer}
                    hasText={currentQuestionHasText}
                    pending={submit.isPending}
                    error={answerError}
                    found={scratchFound}
                    crossed={scratchCrossed}
                    onSubmit={submitQcm}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Chasse aux indices : barre sticky bottom minimaliste (intro + chrono).
            Les CASES sont dans le bloc clue_hunt rendu inline via
            StatementBlockBody. Le CluePopup est en bas absolu (fixed). */}
          {activeClueHunt && <ClueHuntStickyBar />}
          {activeClueHunt && <CluePopupFromCtx />}
          {activeClueHunt && <FloatingXpBadgeFromCtx />}
        </div>
      </ClueHuntProviderWrapper>

      {/* Bouton flottant : UNIQUEMENT pour les étapes de lecture (sans QCM) —
          elles n'ont pas de popup à taper. Sur une étape QCM, c'est le tap qui
          ferme la popup de feedback qui fait office de « Suivant ». */}
      <NextButton
        visible={canAdvance && !currentCheckpoint}
        isLast={currentIdx >= totalSteps - 1}
        lastLabel={
          demoMode
            ? 'Continuer →'
            : locked && !skillcardSampleEnd
              ? 'Débloquer la suite →'
              : "Terminer l'exercice"
        }
        onClick={goNext}
      />
    </>
  );
}

// ─── Exercice « sur la feuille » : QCM au clavier du dock ─────────────────────

/** La palette de la feuille de brouillon, dans le dock (on écrit une ligne). */
function PaletteInDock({ session, onDone }: { session: ScratchSession; onDone: () => void }) {
  const state = useSessionState(session);
  return <SheetPalette state={state} session={session} onClose={onDone} />;
}

/**
 * Une question QCM jouée « sur la feuille » : les propositions longues écrites
 * sous la question, et le dock du bas (touches + Check, palette quand on écrit,
 * attente pendant une chasse aux indices).
 */
function PaperQuestionAnswer({
  checkpoint,
  blocks,
  seed,
  answer,
  hasText,
  pending,
  error,
  found,
  crossed,
  onSubmit,
  mode,
  session,
  canWrite,
  onWrite,
  onDoneWriting,
  writePanel = null,
  onNext,
  nextLabel,
  hideDock = false,
}: {
  checkpoint: ExerciseBlock;
  blocks: ExerciseBlock[];
  seed: number;
  answer: AnswerRecord | undefined;
  hasText: boolean;
  pending: boolean;
  error: string | null;
  found?: string[];
  crossed?: string[];
  onSubmit: (chosenIds: string[]) => void | Promise<void>;
  mode: DockMode;
  session: ScratchSession | null;
  canWrite: boolean;
  onWrite: () => void;
  onDoneWriting: () => void;
  /** clavier de la feuille du moteur v2 (interface du prototype, lot V4) à la place de la palette */
  writePanel?: ReactNode;
  /** réponse envoyée : passer à la suite (la correction est écrite sur la feuille) */
  onNext?: () => void;
  nextLabel?: string;
  /** fin de démo : le tampon du prof est affiché, plus de touches */
  hideDock?: boolean;
}) {
  const qcm = usePaperQcm({ checkpoint, blocks, seed, answer, hasText, pending, onSubmit });
  // prise en main : une fois par appareil, dès que les touches sont là
  const [tutoAsked, setTutoAsked] = useState(false);
  const [tutoAuto] = useState(() => !hasSeenPaperTutorial());
  const [tutoDone, setTutoDone] = useState(false);
  const showTuto =
    mode === 'answer' && qcm.shown && !qcm.revealed && (tutoAsked || (tutoAuto && !tutoDone));
  return (
    <>
      {showTuto && (
        <PaperTutorial
          free={!!session?.free}
          onDone={() => {
            setTutoAsked(false);
            setTutoDone(true);
          }}
        />
      )}
      <PaperChoiceList qcm={qcm} />
      {/* Réponse envoyée : la note du prof est sur la feuille, plus de touches — un petit « Suivant » en bas à droite. */}
      {!hideDock && mode === 'sent' ? (
        onNext ? (
          <PaperNextButton label={nextLabel ?? 'Suivant'} onClick={onNext} />
        ) : null
      ) : !hideDock ? (
        <AnswerDock label={mode === 'write' ? 'Feuille de brouillon' : 'Ta réponse'}>
          {mode === 'write' && session ? (
            (writePanel ?? <PaletteInDock session={session} onDone={onDoneWriting} />)
          ) : mode === 'clue' ? (
            <p className="font-display py-3 text-center text-sm font-semibold text-[var(--sc-muted)]">
              Trouve les indices dans l’énoncé pour débloquer les réponses.
            </p>
          ) : (
            <AnswerKeys
              qcm={qcm}
              found={found}
              crossed={crossed}
              error={error}
              onWrite={canWrite ? onWrite : undefined}
              onHelp={() => setTutoAsked(true)}
            />
          )}
        </AnswerDock>
      ) : null}
    </>
  );
}

// ─── Bouton « Suivant » flottant ──────────────────────────────────────────────
// Fixe en bas-droite. Caché (et non simplement désactivé) tant qu'une question
// active n'a pas reçu de réponse — l'utilisateur avance en cliquant un choix
// du QCM, le bouton apparaît ensuite pour passer à l'étape suivante.

function NextButton({
  visible,
  isLast,
  lastLabel = "Terminer l'exercice",
  onClick,
}: {
  visible: boolean;
  isLast: boolean;
  lastLabel?: string;
  onClick: () => void;
}) {
  if (!visible) return null;
  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-end p-4"
      style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
    >
      <button
        type="button"
        onClick={onClick}
        className="bg-accent-400 font-display hover:bg-accent-500 pointer-events-auto min-h-11 rounded-2xl px-6 py-2.5 text-sm font-bold text-[#0a2e57] shadow-lg transition-all active:scale-[.98]"
      >
        {isLast ? lastLabel : 'Suivant →'}
      </button>
    </div>
  );
}

// ─── Carte d'une étape passée ─────────────────────────────────────────────────

export function PastStepCard({
  block,
  checkpoint,
  answer,
  blocks,
  showPrompt = true,
  stepInfo = null,
  frozenClueHunts,
  onReport,
  scratch,
  paper = false,
}: {
  block: ExerciseBlock;
  checkpoint: ExerciseBlock | null;
  /** Lignes de la feuille de brouillon, affichées avant la réponse. */
  scratch?: ReactNode;
  /** Exercice « sur la feuille » : écrit sur le papier, sans carte ; ✓/✗ dans la marge. */
  paper?: boolean;
  answer: AnswerRecord | undefined;
  blocks: ExerciseBlock[];
  /** « Signaler cette question » (migration 0348) — question répondue seulement. */
  onReport?: () => void;
  /** Afficher l'énoncé de la question (faux pour les étapes ≥ 2 d'une même question). */
  showPrompt?: boolean;
  /** Libellé « Étape i / n » quand la question a plusieurs étapes. */
  stepInfo?: { index: number; count: number } | null;
  /** State FIGÉ des chasses terminées (pour rendu historique read-only). */
  frozenClueHunts?: Map<string, { revealed: string[]; earned: Array<[string, number]> }>;
}) {
  const [expanded, setExpanded] = useState(false);
  const isAnsweredQuestion = !!checkpoint && !!answer;
  const wasCorrect = isAnsweredQuestion ? answer.was_correct : null;
  // Chasse aux indices enfant de cette question (rendue quand expanded).
  const clueHuntChild = blocks.find(
    (b) => b.block_type === 'clue_hunt' && b.parent_id === block.id,
  );
  const frozenState = clueHuntChild ? frozenClueHunts?.get(clueHuntChild.id) : undefined;

  // Bloc `statement` (pas de QCM) — toujours intégralement visible, carte plate.
  if (!checkpoint) {
    return (
      <div className={paper ? 'w-full py-2' : 'border-ink-100 w-full border bg-white px-4 py-3'}>
        <StatementBlockBody block={block} />
      </div>
    );
  }

  // Question répondue : carte verte (correct) ou rouge (faux) + badge ✓/✗.
  // Multi-correct : plusieurs bonnes réponses possibles + plusieurs choix cochés.
  const distractors = childrenOf(blocks, checkpoint.id).filter(
    (b) => b.block_type === 'distractor',
  );
  const correctIds = new Set(correctChoiceIds(blocks, checkpoint));
  const labelOf = (b: ExerciseBlock): string =>
    b.id === checkpoint.id
      ? String((checkpoint.data as { answer_md?: string }).answer_md ?? '?')
      : String((b.data as { label_md?: string }).label_md ?? '?');
  const correctLabels = [checkpoint, ...distractors.filter((d) => correctIds.has(d.id))].map(
    labelOf,
  );
  const chosenIds = answer?.chosen_ids ?? (answer ? [answer.chosen_id] : []);
  const chosenWrongLabels = chosenIds
    .map((id) => blocks.find((b) => b.id === id))
    .filter((b): b is ExerciseBlock => !!b && !correctIds.has(b.id))
    .map(labelOf);
  const questionData = block.data as { prompt_md?: string };

  const card = (
    <button
      type="button"
      onClick={() => setExpanded((v) => !v)}
      className={
        paper
          ? 'relative w-full space-y-2 py-2 pr-10 text-left'
          : 'border-ink-100 relative w-full space-y-2 border bg-white px-4 py-3 pr-12 text-left transition-transform active:scale-[.98]'
      }
    >
      {showPrompt && questionData.prompt_md && (
        <div className="text-ink-800 font-sans text-sm">
          <MarkdownMath>{airSentences(questionData.prompt_md)}</MarkdownMath>
        </div>
      )}

      {stepInfo && (
        <p className="text-ink-400 font-sans text-[11px] font-semibold uppercase tracking-wider">
          Étape {stepInfo.index + 1} / {stepInfo.count}
        </p>
      )}

      {stepPromptOf(checkpoint) && (
        <div className="text-ink-700 font-sans text-sm font-medium">
          <MarkdownMath>{stepPromptOf(checkpoint)}</MarkdownMath>
        </div>
      )}

      {/* Chasse aux indices — placée AVANT la bonne réponse pour rester
          proche du prompt de la question. Reste visible même carte collapsed
          en mode read-only (frozen) : le provider a été démonté à la fin de
          la chasse mais les indices révélés sont conservés dans frozenState. */}
      {clueHuntChild && (
        <ClueHuntCasesInline
          data={clueHuntChild.data as unknown as ClueHuntData}
          frozen={frozenState}
        />
      )}

      {scratch}

      {correctLabels.map((lbl, i) => (
        <div key={`ok-${i}`} className="flex items-start gap-2">
          <span className="text-xp-600 mt-0.5 shrink-0 text-xs font-bold">✓</span>
          <div className="text-xp-700 flex-1 font-sans text-sm">
            <MarkdownMath>{lbl}</MarkdownMath>
          </div>
        </div>
      ))}

      {expanded &&
        !wasCorrect &&
        chosenWrongLabels.map((lbl, i) => (
          <div key={`ko-${i}`} className="flex items-start gap-2">
            <span className="text-error-600 mt-0.5 shrink-0 text-xs font-bold">✗</span>
            <div className="text-error-700 flex-1 font-sans text-sm">
              <MarkdownMath>{lbl}</MarkdownMath>
            </div>
          </div>
        ))}

      {expanded && <CorrectionInline checkpoint={checkpoint} />}

      <span
        aria-hidden="true"
        className={cn(
          paper
            ? 'pp-mark top-2'
            : 'absolute bottom-3 right-3 flex h-8 w-8 items-center justify-center text-2xl font-extrabold',
          wasCorrect ? 'text-xp-500' : 'text-error-500',
        )}
      >
        {wasCorrect ? '✓' : '✗'}
      </span>
    </button>
  );
  if (!onReport || !isAnsweredQuestion) return card;
  // Bouton « Signaler » À CÔTÉ de la carte (jamais un bouton dans un bouton).
  return (
    <div className={paper ? 'relative' : 'relative overflow-hidden'}>
      {card}
      <button
        type="button"
        onClick={onReport}
        aria-label="Signaler cette question"
        title="Signaler un problème ou proposer une amélioration"
        className="text-ink-400 hover:text-ink-700 absolute right-0 top-0 flex h-11 w-11 items-center justify-center active:scale-95"
      >
        <Flag className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}

// ─── QCM (rendu des options) ──────────────────────────────────────────────────

/** Délai (ms) entre l'affichage d'une question et la révélation de ses choix. */
export const QCM_REVEAL_DELAY_MS = READING_REVEAL_DELAY_MS;

/**
 * Révèle les choix d'un QCM après `QCM_REVEAL_DELAY_MS` (le temps de lire la
 * question). Révélation IMMÉDIATE (pas de délai) si :
 *   - la question est déjà répondue (retour en arrière), ou
 *   - la question n'a aucun texte à lire (`hasText=false`).
 * Fin wrapper autour du hook partagé `useDelayedReveal` (`@jeraisonne/ui`).
 */
export function useDelayedChoiceReveal(
  checkpointId: string,
  revealed: boolean,
  hasText = true,
): boolean {
  return useDelayedReveal({ key: checkpointId, immediate: revealed || !hasText });
}

/** Réponse qui n'a pas pu être enregistrée (réseau coupé, serveur lent…). */
export const ANSWER_NOT_SENT = 'Ta réponse n’est pas partie. Vérifie ta connexion puis réessaie.';

export function QcmChoices({
  checkpoint,
  blocks,
  seed,
  answer,
  hasText,
  pending = false,
  error = null,
  found,
  crossed,
  onSubmit,
}: {
  checkpoint: ExerciseBlock;
  blocks: ExerciseBlock[];
  seed: number;
  answer: AnswerRecord | undefined;
  /** Choix que la feuille de brouillon de l'élève établit (« ✓ Dans ton brouillon »). */
  found?: string[];
  /** Feuille d'affirmations (moteur v36) : choix que le brouillon montre faux. */
  crossed?: string[];
  /** La question affichée a-t-elle un texte à lire ? Faux ⇒ pas de délai. */
  hasText: boolean;
  /** Une soumission est en cours (désactive les interactions). */
  pending?: boolean;
  /** La dernière réponse n'a pas pu partir : message affiché sous les choix. */
  error?: string | null;
  /**
   * Reçoit l'ENSEMBLE coché : [id] en single (clic immédiat), [...] en multiple
   * (via Valider). Peut renvoyer une promesse : les choix restent bloqués, avec
   * un indicateur sur le choix touché, jusqu'à ce qu'elle se termine.
   */
  onSubmit: (chosenBlockIds: string[]) => void | Promise<void>;
}) {
  const mode = checkpointAnswerMode(checkpoint);
  const multiple = mode === 'multiple';
  const options = useMemo(
    () => buildQcmOptions(checkpoint, blocks, seed),
    [checkpoint, blocks, seed],
  );

  const revealed = !!answer;

  // Sélection courante (mode multiple, avant validation). Réinitialisée à chaque
  // changement de checkpoint (le composant n'est PAS remonté d'un pas à l'autre).
  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => {
    setSelected(new Set());
  }, [checkpoint.id]);

  const chosenSet = useMemo(() => new Set(answer?.chosen_ids ?? []), [answer]);

  // Laisse le temps de LIRE la question avant d'exposer les choix : on attend
  // 2,5 s après l'affichage d'une question, puis on révèle les options.
  const choicesShown = useDelayedChoiceReveal(checkpoint.id, revealed, hasText);

  // Envoi en cours : choix touché (spinner) et garde contre un 2e toucher qui
  // arriverait avant que l'état ne soit rendu.
  const [sendingIds, setSendingIds] = useState<string[] | null>(null);
  const sendingRef = useRef(false);
  useEffect(() => {
    setSendingIds(null);
    sendingRef.current = false;
  }, [checkpoint.id]);
  const busy = pending || sendingIds !== null;

  const send = (ids: string[]) => {
    if (revealed || sendingRef.current || ids.length === 0) return;
    sendingRef.current = true;
    setSendingIds(ids);
    let result: void | Promise<void>;
    try {
      result = onSubmit(ids);
    } catch {
      result = undefined;
    }
    void Promise.resolve(result)
      .catch(() => undefined)
      .finally(() => {
        sendingRef.current = false;
        setSendingIds(null);
      });
  };

  const handleClick = (id: string) => {
    if (revealed || busy) return;
    if (multiple) {
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    } else {
      send([id]);
    }
  };

  return (
    <div
      className={cn(
        'space-y-2',
        // Transition appliquée UNIQUEMENT au fondu d'ENTRÉE (1 s). À l'état
        // caché, aucune classe de transition → le passage à `opacity-0` est
        // instantané : sans ça, en changeant de question, l'élément (même nœud
        // DOM, pas de remount) animerait un fondu de SORTIE et les choix de la
        // nouvelle question clignoteraient avant de disparaître.
        choicesShown
          ? 'translate-y-0 opacity-100 transition-all duration-1000 ease-out'
          : 'pointer-events-none translate-y-2 opacity-0',
      )}
      aria-hidden={!choicesShown}
    >
      {/* Indicateur du mode de réponse. */}
      <p className="text-ink-400 text-[11px] font-semibold uppercase tracking-wider">
        {multiple ? 'Plusieurs réponses possibles' : 'Réponse unique'}
      </p>

      {options.map((opt, i) => {
        const isChosen = revealed ? chosenSet.has(opt.id) : selected.has(opt.id);
        return (
          <ChoiceCard
            key={opt.id}
            label={opt.label}
            visuals={opt.visuals}
            letter={String.fromCharCode(65 + i)}
            isCorrect={opt.is_correct}
            isChosen={isChosen}
            revealed={revealed}
            multiple={multiple}
            disabled={revealed || busy}
            loading={!revealed && !multiple && !!sendingIds?.includes(opt.id)}
            foundInDraft={!revealed && !!found?.includes(opt.id)}
            crossedInDraft={!revealed && !!crossed?.includes(opt.id)}
            onClick={() => handleClick(opt.id)}
          />
        );
      })}

      {/* Bouton « Valider » — mode multiple uniquement, avant révélation. */}
      {multiple && !revealed && (
        <button
          type="button"
          disabled={selected.size === 0 || busy}
          aria-busy={sendingIds !== null || undefined}
          onClick={() => send([...selected])}
          className={cn(
            'bg-accent-400 font-display hover:bg-accent-500 mt-1 min-h-11 w-full rounded-2xl px-6 py-2.5 text-sm font-bold text-[#0a2e57] shadow-sm transition-all active:scale-[.98] disabled:cursor-not-allowed',
            sendingIds !== null ? 'disabled:opacity-80' : 'disabled:opacity-40',
          )}
        >
          <span className="flex items-center justify-center gap-2">
            {sendingIds !== null && <Spinner className="h-4 w-4" />}
            {sendingIds !== null ? 'Envoi de ta réponse…' : 'Valider'}
          </span>
        </button>
      )}

      {error && !revealed && !busy && (
        <p role="alert" className="text-error-600 font-sans text-sm font-semibold">
          {error}
        </p>
      )}
    </div>
  );
}

// ─── Bouton de choix (avec badge ✓/✗ en bas à droite après réponse) ──────────

function ChoiceCard({
  label,
  visuals,
  letter,
  isCorrect,
  isChosen,
  revealed,
  multiple = false,
  disabled,
  loading = false,
  foundInDraft = false,
  crossedInDraft = false,
  onClick,
}: {
  label: string;
  /** Réponse touchée en cours d'envoi : spinner + mise en évidence. */
  loading?: boolean;
  /** Visuels embarqués de l'option (ex. graphiques d'un QCM « choisis le bon graphe »). */
  visuals?: ExerciseVisualBlock[];
  letter: string;
  isCorrect: boolean;
  isChosen: boolean;
  revealed: boolean;
  /** QCM à réponses multiples : `isChosen` reflète la sélection AVANT validation. */
  multiple?: boolean;
  disabled: boolean;
  /** la feuille de brouillon de l'élève mène à ce choix */
  foundInDraft?: boolean;
  /** feuille d'affirmations (moteur v36) : le brouillon montre que ce choix est faux */
  crossedInDraft?: boolean;
  onClick: () => void;
}) {
  let cls =
    'relative w-full text-left rounded-xl border px-4 py-3 pr-12 text-sm transition-colors active:scale-[.98] min-h-[2.75rem] ';

  if (!revealed) {
    // En multiple, un choix sélectionné (non encore validé) est mis en évidence ;
    // en single, le choix touché pendant l'envoi.
    cls +=
      (multiple && isChosen) || loading
        ? 'border-primary-400 bg-primary-50 ring-1 ring-primary-300'
        : disabled
          ? 'border-ink-200 bg-white opacity-60'
          : 'border-ink-200 bg-white hover:border-primary-300 hover:bg-ink-50';
  } else if (isCorrect) {
    cls += 'border-xp-300 bg-xp-50 font-medium';
  } else if (isChosen) {
    cls += 'border-error-300 bg-error-50';
  } else {
    cls += 'border-ink-100 bg-white opacity-50';
  }

  const showBadge = revealed && (isCorrect || isChosen);

  return (
    <button onClick={onClick} disabled={disabled} aria-busy={loading || undefined} className={cls}>
      <div className="flex items-start gap-2.5">
        <span
          className={cn(
            'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-[10px] font-bold',
            // Case à cocher (multiple, avant révélation) vs pastille lettre.
            multiple && !revealed ? 'rounded-md border' : 'rounded-full',
            multiple && !revealed && isChosen
              ? 'border-primary-500 bg-primary-500 text-white'
              : revealed && isCorrect
                ? 'bg-xp-500 rounded-full text-white'
                : multiple && !revealed
                  ? 'border-ink-300 text-ink-500 bg-white'
                  : 'bg-ink-100 text-ink-500',
          )}
        >
          {multiple && !revealed && isChosen ? '✓' : letter}
        </span>
        <div className="min-w-0 flex-1">
          <MarkdownMath>{label}</MarkdownMath>
          {foundInDraft && (
            <span className="font-display bg-xp-50 text-xp-600 mt-1.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[12px] font-bold">
              ✓ Dans ton brouillon
            </span>
          )}
          {crossedInDraft && (
            <span className="font-display bg-error-50 text-error-600 mt-1.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[12px] font-bold">
              ✗ Fausse d’après ton brouillon
            </span>
          )}
          {visuals && visuals.length > 0 && (
            <div className="mt-2">
              <VisualBlocksRenderer blocks={visuals} />
            </div>
          )}
        </div>
      </div>
      {loading && (
        <span className="text-primary-500 absolute bottom-2 right-2 flex h-7 w-7 items-center justify-center">
          <Spinner className="h-5 w-5" />
          <span className="sr-only">Envoi de ta réponse…</span>
        </span>
      )}
      {showBadge && (
        <span
          aria-hidden="true"
          className={cn(
            'absolute bottom-2 right-2 flex h-7 w-7 items-center justify-center text-xl font-extrabold',
            isCorrect ? 'text-xp-500' : 'text-error-500',
          )}
        >
          {isCorrect ? '✓' : '✗'}
        </span>
      )}
    </button>
  );
}

// ─── Explication d'une correction (inline) ───────────────────────────────────

function CorrectionInline({ checkpoint }: { checkpoint: ExerciseBlock }) {
  const data = checkpoint.data as { explanation_md?: string };
  if (!data.explanation_md) return null;
  return (
    <div className="text-ink-700 font-sans text-sm">
      <p className="text-ink-500 mb-1 text-xs font-semibold uppercase tracking-wider">
        Explication
      </p>
      <MarkdownMath>{data.explanation_md}</MarkdownMath>
    </div>
  );
}

// ─── Récap du corrigé complet (fin d'exo) ────────────────────────────────────

function CorrectionRecap({ blocks }: { blocks: ExerciseBlock[] }) {
  const corrSection = blocksInSection(blocks, 'correction');
  return (
    <div className="mt-3 space-y-2">
      {corrSection.map((b) => {
        if (b.block_type !== 'correction') return null;
        const data = b.data as { explanation_md?: string; answer_md?: string; prompt_md?: string };
        return (
          <div key={b.id} className="border-ink-200 rounded-md border bg-white p-3 text-sm">
            {data.prompt_md?.trim() && (
              <p className="text-ink-900 mb-1 font-medium">
                <MarkdownMath>{data.prompt_md}</MarkdownMath>
              </p>
            )}
            <MarkdownMath>{data.explanation_md ?? ''}</MarkdownMath>
            {data.answer_md ? (
              <p className="text-xp-700 mt-1 text-xs font-semibold">→ {data.answer_md}</p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Monte ClueHuntProvider quand une chasse est active, sinon rend juste les
 * enfants sans context. Évite d'ajouter le provider en permanence et permet
 * de réinitialiser le state à chaque changement de chasse (via `key`).
 */
function ClueHuntProviderWrapper({
  clueHunt,
  scopeRef,
  onCompleted,
  children,
}: {
  clueHunt: ExerciseBlock | null;
  scopeRef: React.RefObject<HTMLDivElement | null>;
  onCompleted: (
    final: { totalXp: number; revealed: string[]; earned: Array<[string, number]> },
    id: string,
  ) => void;
  children: React.ReactNode;
}) {
  if (!clueHunt) return <>{children}</>;
  return (
    <ClueHuntProvider
      key={clueHunt.id}
      data={clueHunt.data as unknown as ClueHuntData}
      scopeRef={scopeRef}
      onCompleted={(final) => onCompleted(final, clueHunt.id)}
    >
      {children}
    </ClueHuntProvider>
  );
}

/** Rend la CluePopup depuis le context (bottom absolu). */
function CluePopupFromCtx() {
  const ctx = useClueHuntCtx();
  if (!ctx?.popup) return null;
  return (
    <CluePopup
      key={ctx.popup.key}
      revealedMd={ctx.popup.revealed_md}
      xpEarned={ctx.popup.xp}
      onDismiss={ctx.dismissPopup}
    />
  );
}

/** Rend le FloatingXpBadge depuis le context (position VIEWPORT du clic). */
function FloatingXpBadgeFromCtx() {
  const ctx = useClueHuntCtx();
  if (!ctx?.floatingXp) return null;
  return (
    <FloatingXpBadge
      key={ctx.floatingXp.key}
      x={ctx.floatingXp.x}
      y={ctx.floatingXp.y}
      xp={ctx.floatingXp.xp}
      onDone={ctx.dismissFloatingXp}
    />
  );
}
